import { LATEST_BLOGS_1_WIDGET_DEFAULTS } from "@/constants/admin/fields/latestBlogs/latest_blogs_1";
import { LATEST_BLOGS_2_WIDGET_DEFAULTS } from "@/constants/admin/fields/latestBlogs/latest_blogs_2";
import { POPULAR_BLOGS_1_WIDGET_DEFAULTS } from "@/constants/admin/fields/popularBlogs/popular_blogs_1";
import type {
  IAbout,
  IAbout2,
  IAbout3,
  IAcademicDeliverables,
  IAcademicDeliverables1001,
  IAcademicDeliverables1002,
  IAcademicDeliverables1006,
  IAcademicDeliverables2,
  IAccordion,
  IAccreditation,
  IApplicationProcess,
  IAuthorByline,
  IAuthorCard,
  IAuthorProfile,
  IBanner,
  IBanner1010,
  IBanner15,
  IBanner3,
  IBanner8,
  IBannerVideo,
  IBlogInlineImage,
  IBlogList,
  IBlogWithLead,
  IBrand,
  IBreadcrumbBanner,
  ICareerProspects,
  ICareerProspects2,
  ICareerProspects3,
  ICareerProspects4,
  ICaseStudy,
  ICertification,
  IComparison,
  ICounseling,
  ICounselorCallCta,
  ICourseOutcome,
  ICourseSpecialisations,
  IDigitalLearning,
  IElective,
  IEligibility,
  IEligibility2,
  IEnrollments,
  IEnrollments2,
  IEnrollments3,
  IEnrollments4,
  IEnrollments5,
  IFaculty,
  IFaculty2,
  IFaculty3,
  IFaculty4,
  IFaculty5,
  IFaculty6,
  IFaq,
  IFaq2,
  IFaq3,
  IFaq4,
  IFooter,
  IGrievanceForm,
  IHeader1,
  IHeader1046,
  IHeader2,
  IHeader3,
  IHeader4,
  IHeader5,
  IHeader5001,
  IHeader6,
  IHeader7,
  IHeaderPromoPill,
  IHeading,
  IInfluencerForm,
  IInternshipAbout,
  IInternshipHeading,
  IInternshipCourses,
  IInternshipRegistrationProcess,
  IInfluencerForm1007,
  IInfluencerForm1049,
  IInfluencerForm1072,
  IInstitutes,
  IKeyNumbers,
  IKeyNumbers2,
  ILatestBlogs,
  ILeaders,
  ILearnFromLeaders,
  ILifeMilestone,
  ILifeMilestone2,
  ILifeMilestone3,
  ILiveLectureConfig,
  IMasterclass,
  IMenuItem,
  IMenuItem1047,
  IMenuItem2,
  IMenuItem2000,
  IMenuItem3,
  IMenuItem4,
  IMenuItem5,
  IMobileApp,
  IMobileLeadForm,
  IMobileLeadForm2,
  IParaText,
  IPlacements,
  IPlacements2,
  IPlacements3,
  IPlacements4,
  IPopularBlogs,
  IProgramBenefits,
  IProgramBenefits2,
  IProgramCards,
  IProgramFee,
  IProgramFeeV2,
  IProgramProspects,
  IProgramStructure,
  IProgramStructure1042,
  IProgramStructure3,
  IProgramStructure4,
  IProgramStructure5,
  IProgramStructureV2,
  IRecruiters,
  IReferralForm,
  IReferralForm1008,
  IReferralForm1048,
  IReferralForm1071,
  IRichTextEditor,
  IScholar,
  ISkillCards,
  ISkills,
  ISpecialisation,
  ISpecialisationCarousel,
  ISubscribe,
  ISuccessStories,
  ITestimonials,
  ITestimonials2,
  IThankYou,
  IThankYou1073,
  IViceChancellor,
  IVisionaries,
  IWalkThrough,
  IWhatMakesCourseDifferent,
  IWhoIsThisProgramFor,
  PageState,
} from "@/types/page";
import {
  LFL_DEFAULT_DESCRIPTION,
  LFL_DEFAULT_DESCRIPTION_COLOR,
  LFL_DEFAULT_HEADING_LINE_1,
  LFL_DEFAULT_HEADING_LINE_2,
} from "@/widgets/visionaries/learnFromLeadersCopy";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// Re-export types for backward compatibility
export type { PageState } from "@/types/page";

/** Strip non–Banner-3 fields from API / form payloads so Redux stays minimal. */
function normalizeBanner3Payload(p: Record<string, unknown>): IBanner3 {
  const str = (key: string, fallback: string) =>
    p[key] !== undefined && p[key] !== null ? String(p[key]) : fallback;

  const rawMeta = p.metadata;
  const metadata = Array.isArray(rawMeta)
    ? (rawMeta as Record<string, unknown>[])
    : [];
  const rawSf = p.slide_frequency;
  const slide_frequency =
    typeof rawSf === "number" && Number.isFinite(rawSf)
      ? Math.max(500, rawSf)
      : Math.max(500, parseInt(String(rawSf ?? "5000"), 10) || 5000);

  return {
    id: str("id", ""),
    status: typeof p.status === "boolean" ? p.status : true,
    metadata,
    slide_frequency,
    hero_video_url: str("hero_video_url", ""),
    accreditation_image_desktop: str("accreditation_image_desktop", ""),
    accreditation_image_mobile: str("accreditation_image_mobile", ""),
    title_1: str("title_1", "Education That"),
    title_1_color: str("title_1_color", "rgba(255,255,255,0.8)"),
    title_2: str("title_2", "Transforms"),
    title_2_gradient_start_color: str(
      "title_2_gradient_start_color",
      "#342B7C",
    ),
    title_2_gradient_end_color: str("title_2_gradient_end_color", "#02A7B6"),
    title_2_gradient_angle: str("title_2_gradient_angle", "90deg"),
    subtitle: str(
      "subtitle",
      "We University brings premium, industry-aligned learning online. Build skills that matter, work on real challenges, and unlock your next big move.",
    ),
    subtitle_color: str("subtitle_color", "#FFFFFF"),
    apply_now_text: str("apply_now_text", "Apply Now"),
    apply_now_color: str("apply_now_color", "#222222"),
    button_color: str("button_color", "#FFFFFF"),
    explore_cta_url: str("explore_cta_url", ""),
    background_color: str("background_color", "#000000"),
    download_brochure_text: str("download_brochure_text", ""),
  };
}

// Update initial state
const initialState: PageState = {
  banner_1: {
    id: "",
    slide_frequency: 0,
    status: false,
    metadata: [],
  },
  banner_2: {
    id: "",
    status: true,
    metadata: [],
    metadata_1: [],
    metadata_2: [],
    metadata_3: [],
    // Banner 2 specific fields
    title_1: "Online course in",
    title_1_color: "#FFFFFFCC",
    title_2: "Financial Planning and Applied Finance",
    title_2_gradient_start_color: "#FFFFFF",
    title_2_gradient_end_color: "#00D4EB",
    title_2_gradient_angle: "90deg",
    subtitle: "with CFP® Global Certification program",
    subtitle_color: "#FFFFFFCC",
    horizontal_stripe_color: "#FFFFFF",
    font_family_variant: "variant_2",
    backvariant: "variant_1",
    gradient_color_1: "#00D4EB",
    gradient_color_2: "#000000A8",
    gradient_color_3: "#000000A8",
    gradient_color_4: "#5345C3",
    background_color: "#000000",
    learners_description: "<p>Join <strong>10k+</strong> alumni</p>",
    carousel_color: "#c8faff",
    accreditation_scroll_duration: 20000,
    show_rating: true,
    show_learners: true,
    rating_number: "4/5",
    rating_text: "(7812 ratings)",
    rating_color: "#FFFFFF",
    rating_number_color: "#FFFFFF",
    download_brochure_text: "Download Brochure",
    button_color: "#FFFFFF",
    icon: "",
    alt_icon: "Download icon",
    icon_color: "#222222",
    apply_now_text: "Apply Now",
    apply_now_color: "#FFFFFF",
    apply_now_border_color: "#FFFFFF",
  },
  banner_3: normalizeBanner3Payload({}),
  banner_4: {
    id: "",
    status: true,
    slide_frequency: 7000,
    metadata: [],
    metadata_1: [],
    hero_section_height: 564,
    hero_container_max_width: 1114,
    background_color: "#291973",
    hero_info_bar_show: true,
    hero_info_bar_text:
      "8 Months ✦ 100% Live Online ✦ Batch Start Date: 30 June 2026",
    apply_now_show: true,
    apply_now_text: "Get in Touch",
    apply_now_link: "",
    download_brochure_show: true,
    download_brochure_text: "Download Brochure",
    download_brochure_link: "",
    application_deadline_show: true,
    application_deadline_text: "Application Deadline – 20 Apr",
    button_color: "#291973",
    apply_now_color: "#ffffff",
    carousel_color: "#ffffff",
  },
  banner_5: {
    id: "",
    status: true,
    metadata: [],
    metadata_1: [],
    hero_section_height: 720,
    hero_container_max_width: 1200,
    title_1: "From Full Stack to AI-Native Systems,",
    title_1_color: "#FFFFFF",
    subtitle:
      "led by IIT Kharagpur's Computer Science & Engineering Department",
    subtitle_color: "#F5C451",
    gradient_color_1: "rgba(0, 0, 0, 0.55)",
    gradient_color_2: "rgba(0, 0, 0, 0.78)",
    background_color: "#0a1628",
  },
  banner_6: {
    id: "",
    status: true,
    metadata: [],
    metadata_2: [],
    hero_section_height: 0,
    hero_container_max_width: 1200,
    title_1: "Capstone: Build One System. Ship to Production",
    title_1_color: "#FFFFFF",
    subtitle:
      "Choose a real-world problem from your domain - fintech, e-commerce, healthcare, or enterprise SaaS, and build a complete AI-native system from architecture to deployment.",
    subtitle_color: "rgba(255,255,255,0.92)",
    learners_description:
      "Design, build, and deploy an end-to-end application integrating frontend, backend, AI systems, and agents - demonstrating true production-grade engineering capability.",
    icon_color: "#F5C451",
    button_color: "#E85D04",
    apply_now_text: "Apply Now",
    apply_now_color: "#FFFFFF",
    apply_now_link: "",
    apply_now_is_cta: true,
    background_image: "",
    alt_background_image: "Hero background",
    background_color: "#0a0a0a",
  },
  banner_8: {
    id: "",
    status: false,
    title: "Key {skills} you'll build",
    description: "",
    image: "",
    mobile_image: "",
    image_alt: "",
    background_color: "#000000",
    title_color: "#FFFFFF",
    title_highlight_color: "#FFD700",
    description_color: "#FFFFFF",
    card_title_color: "#FFFFFF",
    card_description_color: "#FFFFFF",
    divider_color: "#F8FAFC",
    metadata: [],
  },
  banner_15: {
    id: "",
    status: false,
    title: "",
    image: "",
    mobile_image: "",
    image_alt: "",
    title_color: "#FFFFFF",
    background_color: "#FFFFFF",
  },
  banner_1010: {
    id: "",
    status: true,
    title: "",
    title_color: "#FFFFFF",
    title_highlight_color: "#FFD700",
    image: "",
    image_alt: "banner",
    mobile_image: "",
    background_color: "rgba(3, 7, 18, 1)",
    hero_section_height: 564,
    hero_container_max_width: 1114,
  },
  banner_9: {
    id: "",
    status: true,
    slide_frequency: 8000,
    metadata: [],
    image: "",
    mobile_image: "",
    image_alt: "banner-bg",
    hero_section_height: 564,
    hero_container_max_width: 1114,
    background_color: "#030712",
    apply_now_show: true,
    apply_now_text: "Enroll Now",
    apply_now_link: "",
    apply_now_is_cta: true,
    download_brochure_show: true,
    download_brochure_text: "Download Brochure",
    download_brochure_link: "",
    download_brochure_is_cta: true,
    show_lead_form: false,
    enable_lead_form: false,
    button_color: "#E1473F",
    apply_now_color: "#ffffff",
    carousel_color: "#E1473F",
  },
  banner_10: {
    id: "",
    status: true,
    metadata: [],
    metadata_1: [],
    metadata_2: [],
    metadata_3: [],
    background_image: "",
    background_image_mobile: "",
    background_color: "#50040B",
    show_breadcrumbs: false,
    show_breadcrumbs_desktop: false,
    show_breadcrumbs_mobile: false,
    show_eyebrow_badges_mobile: true,
    eyebrow_badge_bg_color: "rgba(255,255,255,0.14)",
    title_1: "India's 1st Online MBA in",
    title_1_color: "#F5F5F5",
    show_title_1_mobile: true,
    title_2: "Product Management",
    title_2_color: "#F5F5F5",
    show_title_2_mobile: true,
    subtitle: "with CFP® Global Certification program",
    subtitle_color: "rgba(255,255,255,0.8)",
    show_subtitle_mobile: true,
    mobile_creative_image: "",
    show_mobile_creative_shadow: false,
    mobile_creative_shadow_color: "rgba(0,0,0,0.35)",
    paragraph_variant: "bullets",
    show_paragraph_mobile: true,
    paragraph_text: "",
    paragraph_color: "#FFFFFF",
    bullet_icon: "",
    collaboration_image: "",
    collaboration_image_mobile: "",
    download_brochure_text: "Download Brochure",
    button_color: "#FFFFFF",
    cta_text_color: "#222222",
    explore_cta_url: "",
    cta_action: "download",
    show_learners: true,
    show_learners_desktop: true,
    show_learners_mobile: true,
    learners_description: "Join 10k+ alumni",
    learners_description_color: "#FFFFFF",
    show_rating: true,
    show_rating_desktop: true,
    show_rating_mobile: true,
    rating_number: "4/5",
    rating_number_color: "#FFFFFF",
    rating_text: "(7.8K ratings)",
    rating_color: "rgba(255,255,255,0.5)",
    enable_lead_form: true,
    enable_animation: false,
  },
  banner_11: {
    id: "",
    status: true,
    metadata: [],
    background_image: "",
    background_image_mobile: "",
    background_color: "#D6DE66",
    left_image: "",
    title_1: "Admissions Open for 2026",
    title_1_color: "#081F3D",
    paragraph_text:
      "Join a new generation of business professionals equipped to navigate change, leverage AI, and create impact.",
    paragraph_color: "#081F3D",
    bullet_icon: "",
    download_brochure_text: "Apply Now",
    button_color: "#081F3D",
    cta_text_color: "#FFFFFF",
    apply_now_text: "Prospectus",
    apply_now_color: "#081F3D",
    apply_now_border_color: "#081F3D",
    enable_animation: false,
  },
  // Banner 3000 shares Banner 1's data contract (copy with a shorter desktop height).
  banner_3000: {
    id: "",
    slide_frequency: 0,
    status: false,
    metadata: [],
  },
  // Banner 1025 — BDU Hero. Defaults mirror the BDU v1 Hero/Banner pair.
  banner_1025: {
    id: "",
    status: true,
    slide_frequency: 5000,
    metadata: [],
    metadata_1: [],
    hero_section_height: 540,
    gradient_color_1: "#13344F",
    gradient_color_2: "#77B6EB",
    background_color: "#22405ADB",
    gradient_color_3: "#FF0013",
    gradient_color_4: "#990013",
    apply_now_show: true,
    apply_now_text: "Enroll Now",
    apply_now_link: "",
    apply_now_is_cta: true,
    apply_now_color: "#FFFFFF",
    horizontal_stripe_color: "#FFFFFF",
  },
  key_numbers_1026: {
    id: "",
    status: true,
    metadata: [],
    background_color: "#836537",
    title_start_color: "#FFFFFF",
    description_color: "#FFFFFF",
    gradient_color_1: "#22202369",
    gradient_color_2: "#FFFFFF",
    gradient_color_3: "#e5e7eb",
  },
  program_cards_1027: {
    id: "",
    status: true,
    metadata: [],
    title: "Explore Our Online Programmes",
    title_text_color: "#222023",
    desktop_background_color: "#FFFFFF",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    gradient_color_3: "#FF0013",
    gradient_color_4: "#990013",
    card_background_color: "#222023A1",
    cta_text: "Know more",
  },
  about_1028: {
    id: "",
    status: true,
    metadata: [],
    title: "About Us",
    title_text_color: "#222023",
    description: "",
    description_text_color: "#4F4F4F",
    desktop_image: "",
    alt_image: "About Us",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    gradient_color_3: "#FF0013",
    gradient_color_4: "#990013",
    is_cta: true,
    read_more_text: "Know More",
    cta_link: "/about-us",
  },
  program_benefits_1029: {
    id: "",
    status: true,
    metadata: [],
    title: "Why Choose Bharathidasan University Online?",
    title_start_color: "#FFFFFF",
    gradient_color_1: "#FFFFFF",
    image: "",
    mobile_image: "",
    section_overlay_rgba: "rgba(14, 40, 57, 0.87)",
  },
  application_process_1030: {
    id: "",
    status: true,
    metadata_1: [],
    title: "Admission process",
    title_start_color: "#000000",
    desktop_background_color: "#FFFFFF",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    accent_color: "#000000",
  },
  academic_deliverables_1031: {
    id: "",
    status: true,
    metadata: [],
    title: "Hands-On Learning with Expert Faculty",
    title_text_color: "#FFFFFF",
    desktop_bg_image: "",
    mobile_bg_image: "",
    section_background_color: "#13344FD4",
    sub_title_text_color: "#A4A4A4",
  },
  leaders_1032: {
    id: "",
    status: true,
    metadata: [],
    title: "Our Leadership",
    title_start_color: "#000000",
    desktop_background_color: "#FFFFFF",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    gradient_color_3: "#E6E6E6",
    gradient_color_4: "#D6D6D687",
  },
  faq_1033: {
    id: "",
    status: true,
    title: "FAQs",
    title_text_color: "#000000",
    section_background_color: "#F7F7F7",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    background_color: "#173551",
    section_color: "#FFFFFF",
    accordion_muted_index_color: "#3C3C4380",
    accordion_answer_surface_bg: "#ECF6FF",
    accordion_answer_surface_border: "#AABFD3",
    gradient_color_3: "#FF0013",
    gradient_color_4: "#990013",
  },
  footer_1034: {
    id: "",
    status: true,
    enable_footer_content: true,
    text_color: "#FFFFFF",
    gradient_start_color: "#5B789F",
    title: "Disclaimer: bduedu.in is owned by bdu.ac.in",
    title_text_color: "#FFFFFF",
    enable_copyright: true,
    copyright_text:
      "Copyright 2026 of Bharathidasan University | All Rights Reserved",
    copyright_text_color: "#FFFFFF",
  },

  // ── BDU programme pages (1035–1045) ────────────────────────────────────────
  // Programme Hero — Banner 1025 layout plus an h2 sub-heading and a brochure CTA.
  banner_1035: {
    id: "",
    status: true,
    slide_frequency: 5000,
    metadata: [],
    metadata_1: [],
    hero_section_height: 540,
    gradient_color_1: "#13344F",
    gradient_color_2: "#77B6EB",
    background_color: "#22405ADB",
    gradient_color_3: "#FF0013",
    gradient_color_4: "#990013",
    apply_now_show: true,
    apply_now_text: "Apply Now",
    apply_now_link: "",
    apply_now_is_cta: true,
    apply_now_color: "#FFFFFF",
    apply_now_border_color: "#FFFFFF",
    download_brochure_show: true,
    download_brochure_text: "Brochure",
    download_brochure_link: "",
    download_brochure_is_cta: true,
    horizontal_stripe_color: "#FFFFFF",
  },
  key_numbers_1036: {
    id: "",
    status: true,
    metadata: [],
    background_color: "#836537",
    description_color: "#FFFFFF",
    gradient_color_1: "#22202369",
    gradient_color_4: "#FFFFFF66",
  },
  about_1037: {
    id: "",
    status: true,
    metadata: [],
    background_color: "#FFFFFF",
    title: "About the programme",
    title_text_color: "#222023",
    description: "",
    description_text_color: "#222023",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    gradient_color_3: "#FD0113",
    gradient_color_4: "#9E0113",
    enable_lead_form: true,
    mobile_apply_now_text: "Enroll Now",
  },

  // ── KUK landing pages (5005) ──────────────────────────────────────────────
  about_5005: {
    id: "",
    status: true,
    metadata: [],
    background_color: "#FFFFFF",
    title: "",
    title_text_color: "#101112",
    title_1_color: "#9F1C33",
    description: "",
    description_text_color: "#101112",
    gradient_color_1: "rgba(159, 28, 51, 0.10)",
    highlight_border_color: "#9F1C33",
    key_highlight_heading_color: "#101112",
    key_highlight_subtext_color: "#4B4B4B",
    key_highlight_divider_color: "#F5F5F5",
    cta_icon: "",
    read_more_background_color: "#FFFFFF",
    read_more_color: "#101112",
  },
  program_cards_1038: {
    id: "",
    status: true,
    metadata: [],
    title: "Specialisations",
    title_text_color: "#222023",
    gradient_color_1: "#FF0013",
    gradient_color_2: "#990013",
    card_background_color: "#222023A1",
    cta_text: "Know more",
  },
  key_numbers_1039: {
    id: "",
    status: true,
    metadata: [],
    background_color: "#836537",
    title_start_color: "#FFFFFF",
    description_color: "#FFFFFF",
    gradient_color_4: "#FFFFFF66",
  },
  program_benefits_1040: {
    id: "",
    status: true,
    metadata: [],
    title: "Key Program Features",
    title_start_color: "#222023",
    gradient_color_1: "#13344F",
    gradient_color_2: "#77B6EB",
    section_background_image: "",
    description_color: "#222023",
  },
  eligibility_1041: {
    id: "",
    status: true,
    title: "Eligibility Criteria",
    title_text_color: "#222023",
    description: "",
    description_text_color: "#FFFFFF",
    background_color: "#836537",
    background_gradient_start_color: "#FF0013",
    background_gradient_end_color: "#990013",
    background_gradient_angle: "90deg",
    button_icon: "",
    button_icon_alt: "Eligibility",
  },
  program_structure_1042: {
    id: "",
    status: true,
    metadata: [],
    title: "Programme Syllabus",
    title_text_color: "#FFFFFF",
    section_background_image: "",
    section_background_image_alt: "",
    container_background_color: "#13344F",
    header_section_color: "#FFFFFF",
    subject_container_color: "#FFFFFF",
    module_active_text_color: "#FFFFFF",
    module_inactive_text_color: "#FFFFFF",
    subjects_column_label: "Subjects",
    credits_column_label: "Semester",
    electives_tab_label: "Elective courses",
    electives_semester_label: "Semester",
    electives_empty_message: "There are no subjects in this elective",
    content_panel_background_color: "#F0F0F0",
    card_background_color: "#FEF0F3",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    gradient_color_3: "#FF0013",
    gradient_color_4: "#990013",
    brochure_cta_text: "Download Brochure",
    brochure_cta_text_color: "#FFFFFF",
    brochure_cta_is_cta: true,
  },
  program_fee_1043: {
    id: "",
    status: true,
    title: "Fee Structure",
    title_text_color: "#222023",
    section_background_image: "",
    other_fee: "",
    background_color: "#836537",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    gradient_color_3: "#F50113",
    gradient_color_4: "#A30113",
    table_header_background_color: "#FFDCDE",
    tabs: [],
    rows: [],
    columns: [],
    values: [],
  },
  program_benefits_1044: {
    id: "",
    status: true,
    metadata: [],
    title: "Learn More",
    title_start_color: "#222023",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    gradient_color_3: "#F60214",
    gradient_color_4: "#A10113",
    desktop_background_color: "#CBCBCB",
    subtitle_color: "#E2E8F0",
    load_more_button: "Read more",
  },
  program_cards_1045: {
    id: "",
    status: true,
    metadata: [],
    pre_title: "Didn't find what you need?",
    pre_title_color: "#222023",
    title: "Explore other Specialisation Courses",
    title_text_color: "#222023",
    gradient_color_1: "#FF0013",
    gradient_color_2: "#990013",
    card_background_color: "#222023A1",
    cta_text: "Know more",
  },
  program_cards_6002: {
    id: "",
    status: true,
    metadata: [],
    title: "Explore Our Programs",
    title_text_color: "#222023",
    desktop_background_color: "#F5F5F5",
    gradient_color_1: "#1F3D56",
    gradient_color_2: "#7CB5E6",
    card_background_color: "#222023A1",
    cta_text: "Know more",
  },
  program_cards_6004: {
    id: "",
    status: true,
    metadata: [],
    pre_title: "Didn't find what you need?",
    pre_title_color: "",
    title: "Explore other courses",
    title_text_color: "#031F44",
    gradient_color_1: "#9F1C33",
    background_image: "",
    background_image_alt: "",
    desktop_background_color: "",
    card_background_color: "#FFFFFF",
    title_start_color: "#031F44",
    cta_text: "View",
    cta_text_color: "#9F1C33",
    cta_background_start_color: "#F7F7F7",
  },
  header_1: {
    id: "",
    status: true,
    background_color: "#ffffff",
    logo: "",
    logo_alt_text: "",
    logo_redirect_enabled: true,
    promotional_text: "Admissions Open for 2025",
    promotional_text_color: "#ffffff",
    promotional_text_bg_color: "#BE2330",
    phone_icon: "",
    phone_icon_alt: "Call us",
    phone_number: "+1 800-123-4567",
    phone_number_text_color: "#BE2330",
    button_name: "Apply Now",
    button_link: "#apply",
    button_icon: "",
    button_icon_alt: "Apply now",
    button_color: "#BE2330",
    button_text_color: "#ffffff",
  },
  header_2: {
    id: "",
    status: false,
    background_color: "#ffffff",
    logo: "",
    logo_alt_text: "",
  },
  header_3: {
    id: "",
    status: false,
    promotional_text: "Admissions are closing on 31st Jan. Avail up to 25% scholarship (valid only for this batch)",
    promotional_text_color: "#FFFFFF",
    auto_scroll_speed: "20000",
    gradient_color_1: "#342B7C",
    gradient_color_2: "#00D4EB",
    phone_icon: "",
    phone_icon_alt: "Phone",
    phone_number: "+91 7973615772",
    phone_number_text_color: "#FFFFFF",
    button_name: "WhatsApp Chat",
    button_link: "#whatsapp",
    button_icon: "",
    button_icon_alt: "WhatsApp",
    button_color: "#4CAF51",
    button_text_color: "#ffffff",
  },
  header_4: {
    id: "",
    status: true,
    promotional_text:
      "Admissions closing soon — {Apply now} for early benefits",
    promotional_text_color: "#FFFFFF",
    link_color: "#FFFFFF",
    gradient_color_1: "#342B7C",
    gradient_color_2: "#00D4EB",
    background_gradient_angle: "90deg",
    link_href: "",
    link_trailing_image: "",
    link_trailing_image_alt: "",
    header_status: true,
  },
  header_5: {
    id: "",
    status: false,
    background_color: "#291973",
    pill_chip_background_colour: "#ffffff1a",
    label_text_color: "#FFFFFF",
    phone_text_color: "#FFFFFF",
    phone_icon: "",
    phone_icon_alt: "Phone",
    metadata: [
    ],
    show_on_mobile: false,
    sticky_to_top: false,
    show_logo: false,
    logo_image: "",
    show_apply_button: false,
    apply_button_text: "Apply",
  },
  header_6: {
    id: "",
    status: false,
    featured_pill_text: "Limited Seats Available",
    featured_pill_show: true,
    featured_pill_bg_color: "#DB5803",
    featured_pill_border_color: "#DB5803",
    featured_pill_text_color: "#FFFFFF",
    featured_pill_sticky: true,
  },
  header_7: {
    id: "",
    status: false,
    background_color: "#291973",
    pill_chip_background_colour: "#ffffff1a",
    label_text_color: "#FFFFFF",
    phone_text_color: "#FFFFFF",
    metadata: [],
    show_on_mobile: false,
    sticky_to_top: false,
    show_logo: false,
    logo_image: "",
    whatsapp_show: true,
    whatsapp_number: "",
    whatsapp_link: "",
    whatsapp_button_text: "WhatsApp Chat",
    whatsapp_button_color: "#4CAF51",
    whatsapp_button_text_color: "#FFFFFF",
    whatsapp_icon: "",
    button_name: "",
    button_link: "",
    button_color: "",
    button_text_color: "",
  },
  header_1046: {
    id: "",
    status: false,
    background_color: "#354D6C",
    pill_background_color: "rgba(255, 255, 255, 0.10)",
    label_text_color: "#FFFFFF",
    phone_text_color: "#FFFFFF",
    phone_icon: "",
    phone_icon_alt: "Phone",
    metadata: [],
    show_on_mobile: false,
    sticky_to_top: true,
    whatsapp_show: true,
    whatsapp_number: "",
    whatsapp_link: "",
    whatsapp_button_text: "WhatsApp Chat",
    whatsapp_button_color: "rgba(76, 175, 81, 0.70)",
    whatsapp_button_text_color: "#FFFFFF",
    whatsapp_icon: "",
  },
  menu_item: {
    id: "",
    parent_id: null,
    title: "",
    slug: "",
    url: "",
    status: true,
    priority: 1,
    text_color: "",
    is_button: false,
    button_color: "",
    button_border_color: "",
    button_font_size: "",
  },
  menu_item_2: {
    id: "",
    parent_id: null,
    title: "",
    slug: "",
    url: "",
    status: true,
    priority: 1,
    text_color: "",
    is_button: false,
    button_color: "",
    button_border_color: "",
    button_font_size: "",
  },
  menu_item_3: {
    id: "",
    parent_id: null,
    title: "",
    slug: "",
    url: "",
    status: true,
    priority: 1,
    text_color: "",
    icon: "",
    icon_alt: "",
    is_button: false,
    button_color: "",
  },
  menu_item_4: {
    id: "",
    parent_id: null,
    title: "",
    slug: "",
    url: "",
    status: true,
    priority: 1,
    text_color: "",
    is_button: false,
    button_color: "",
    button_border_color: "",
    button_font_size: "",
  },
  menu_item_5: {
    id: "",
    parent_id: null,
    title: "",
    slug: "",
    url: "",
    status: true,
    priority: 1,
    text_color: "",
    is_button: false,
    button_color: "",
    button_border_color: "",
    button_font_size: "",
  },
  menu_item_2000: {
    id: "",
    parent_id: null,
    title: "",
    slug: "",
    url: "",
    status: true,
    priority: 1,
    text_color: "",
    icon: "",
    icon_alt: "",
    is_button: false,
    button_color: "",
  },
  menu_item_1047: {
    id: "",
    parent_id: null,
    title: "",
    slug: "",
    url: "",
    status: true,
    priority: 1,
    text_color: "",
    icon: "",
    icon_alt: "",
    is_button: false,
    button_color: "",
  },
  about_1: {
    id: "",
    status: false,
    metadata: [],
    metadata_1: [],
    background_color: "#FFFFFF",
    title_text_color: "#000000",
    description_text_color: "#000000",
    read_more_text: "Read More",
    read_more_color: "#BE2330",
    read_more_background_color: "#FFFFFF",
    // know_more_text: "Know More",
    // know_more_text_color: "#FFFFFF",
    // know_more_button_color: "#BE2330",
    // know_more_icon: "",
    // know_more_icon_alt: "Know more",
    // know_more_url: "",
    enable_lead_form: false,
    is_open_leaddrop: false,
    mobile_apply_now_text: "Apply Now",
    mobile_apply_now_text_color: "#FFFFFF",
    mobile_apply_now_icon: "",
    mobile_apply_now_button_color: "#BE2330",
  },
  about_2: {
    id: "",
    status: false,
    metadata: [],
    title_1: "Program",
    title_1_color: "rgba(255, 255, 255, 0.8)",
    title_2: "Overview",
    title_2_gradient_start_color: "#FFFFFF",
    title_2_gradient_end_color: "#02A7B6",
    title_2_gradient_angle: "90deg",
    description: "",
    description_text_color: "rgba(255, 255, 255, 0.9)",
    backvariant: "variant_1",
    gradient_color_1: "#00D4EB",
    gradient_color_2: "#000000",
    gradient_color_3: "#000000",
    gradient_color_4: "#5345C3",
    background_color: "#000000",
    enable_lead_form: false,
  },
  about_5: {
    id: "",
    status: false,
    title: "About",
    title_text_color: "#000000",
    title_1: "IIT Kharagpur",
    title_1_color: "",
    description: "",
    description_text_color: "",
    desktop_image: "",
    mobile_image: "",
    alt_image: "About",
    read_more_text: "Know More",
    read_more_color: "#FFFFFF",
    read_more_background_color: "",
    cta_link: "",
    image_on_right: false,
    metadata: [],
  },
  about_4: {
    id: "",
    status: false,
    metadata: [],
    footer_gradient_start_color: "#2E1C81",
    footer_gradient_end_color: "#15094C",
    footer_gradient_angle: "147.3",
    footer_gradient_stop_1: "18",
    footer_gradient_stop_2: "58",
    footer_accent_border_color: "#346491",
    footer_text_color: "#FFFFFF",
    footer_copyright_html: "",
  },
  about_3: {
    id: "",
    status: false,
    metadata: [],
    background_color: "#FFFFFF",
    title: "About the Online MBA in Product Management",
    title_text_color: "#101112",
    description: "",
    description_text_color: "#101112",
    sub_text: "",
    sub_text_color: "#101112",
    highlight_border_color: "#9F1C34",
    desktop_image: "",
    mobile_image: "",
    alt_image: "",
    cta_label: "Apply Now",
    cta_icon: "",
    cta_icon_alt: "Apply Now",
    cta_text_color: "#FFFFFF",
    cta_bg_color: "#9F1C34",
    cta_enable_shadow: true,
    show_image: true,
    show_key_highlights: true,
  },
  key_numbers_1: {
    id: "",
    status: false,
    metadata: [],
    background_color: "#BE2330",
  },
  key_numbers_2: {
    id: "",
    status: false,
    metadata: [],
    background_color: "",
    desktop_background_color: "#000000",
    mobile_background_color: "#000000",
    backvariant: "variant_3",
    gradient_color_1: "#02A7B6",
    gradient_color_2: "#000000",
    gradient_color_3: "#000000",
    gradient_color_4: "#5345C3",
    pre_title: "",
    pre_title_color: "#FFFFFF",
    title: "",
    title_start_color: "#FFFFFF",
    title_end_color: "#02A7B6",
    title_gradient_angle: "90deg",
    description: "",
    description_color: "#FFFFFFB2",
    enable_animation: false,
  },
  digital_learning_1: {
    id: "",
    status: false,
    metadata: [],
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_3",
    gradient_color_1: "#00D4EB",
    gradient_color_2: "#000000",
    gradient_color_3: "#000000",
    gradient_color_4: "#5345C3",
    pre_title: "Digital Learning,",
    pre_title_color: "#222222CC",
    title: "Real-World Connections",
    title_start_color: "#342B7C",
    title_end_color: "#02A7B6",
    title_gradient_angle: "180deg",
    description: "description",
    description_color: "#222222CC",
    image: "",
    alt_image: "",
    video: "",
    cta_type: "lead_drop",
    cta_link: "",
    cta_text: "Apply Now",
    cta_text_color: "#FFFFFF",
    cta_backcolor_start_color: "#02A7B6",
    cta_backcolor_end_color: "#342B7C",
    cta_gradient_angle: "180deg",
    cta_icon: "",
    alt_cta_icon: "",
    enable_animation: false,
  },
  accreditation_1: {
    id: "",
    status: false,
    metadata: [],
    background_color: "#FFFFFF",
    title_text_color: "#000000",
    sub_title_text_color: "#000000",
    is_swipe: true,
    left_navigation_icon: "",
    left_navigation_icon_alt: "Left navigation",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Right navigation",
    load_more_text: "Load More",
    load_more_color: "#FFFFFF",
    load_more_background_color: "#000000",
    load_more_icon: "",
    load_more_icon_alt: "Load more",
  },
  accreditation_5: {
    id: "",
    status: false,
    metadata: [],
    background_color: "#F8F8F8",
    title_part_1: "Global",
    title_part_2: "Recognition.",
    title_part_3: "National",
    title_part_4: "Pride.",
    title_primary_color: "#171719",
    title_accent_color: "#E1473F",
  },
  accreditation_3: {
    id: "",
    status: false,
    metadata: [],
    background_color: "#FFFFFF",
    title_primary_color: "#171719",
    title_accent_color: "#E1473F",
    body_text_color: "#373737",
    hero_image: "",
    hero_image_alt: "Campus",
    decoration_image: "",
    decoration_image_alt: "",
    vision_title_part_1: "Our ",
    vision_title_part_2: "Vision",
    vision_description:
      "Through accessible, future-ready online education, we are building a world where knowledge knows no boundaries and learning sparks progress for all.",
    mission_title_part_1: "Our ",
    mission_title_part_2: "Mission",
    mission_description:
      "Democratize education with tech & innovation, ensuring access for all. Foster inclusivity, lifelong learning & a global community of changemakers.",
    values_title_part_1: "Our ",
    values_title_part_2: "Values",
  },
  accreditation_4: {
    id: "",
    status: false,
    metadata: [],
    background_color: "#FFFFFF",
    title_primary_color: "#171719",
    body_text_color: "#373737",
    card_active_bg_color: "#FFEFEE",
    card_inactive_bg_color: "#F4F4F4",
    card_title_color: "#171719",
  },
  accreditation_1000: {
    id: "",
    status: false,
    metadata: [],
    title: "Global {Recognition}",
    background_image_desktop: "",
    background_image_mobile: "",
  },
  accreditation_2: {
    id: "",
    status: false,
    metadata: [],
    background_color: "#FFFFFF",
    title: "Ranking and Recognitions",
    title_text_color: "#101112",
    sub_title: "Industry-aligned curriculum, at par with on-campus programs.",
    sub_title_text_color: "#101112",
    left_navigation_icon: "",
    left_navigation_icon_alt: "Previous",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Next",
  },
  academic_deliverables_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    sub_title_text_color: "#000000",
    button_text: "Learn More",
    button_text_color: "#FFFFFF",
    button_background_color: "#BE2330",
    button_icon: "",
    button_icon_alt: "Learn more",
  },
  academic_deliverables_2: {
    id: "",
    status: false,
    title_text_color: "#FFFFFF",
    sub_title_text_color: "#FFFFFF",
    section_background_color: "#db5803",
    recruiters_title: "Alumni {Work At}",
    metadata: [],
  },
  academic_deliverables_1001: {
    id: "",
    status: false,
    title: "Placement {Success}",
    title_text_color: "#000000",
    sub_title_text_color: "#000000",
    section_background_color: "#FFDBD9",
    highlight_color: "",
    recruiters_title: "Our Hiring {Partners}",
    recruiters_sub_title: "",
    button_text: "Get Started",
    metadata: [],
  },
  academic_deliverables_1002: {
    id: "",
    status: false,
    title: "Placement {Success}",
    title_text_color: "#000000",
    sub_title_text_color: "#000000",
    recruiters_title: "Our learners work at {top companies}",
    recruiters_sub_title: "",
    button_text: "Get Started",
    button_text_color: "#FFFFFF",
    button_background_color: "",
    metadata: [],
  },
  academic_deliverables_1006: {
    id: "",
    status: false,
    title: "Industry {Insights}",
    title_text_color: "#000000",
    highlight_color: "",
    button_text: "Get Started",
    button_text_color: "#FFFFFF",
    button_background_color: "",
    metadata: [],
  },
  scholar_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    metadata: [],
  },
  faq_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    section_background_color: "#F5F5F5",
  },
  faq_2: {
    id: "",
    status: false,
    title: "Frequently Asked Questions",
    title_text_color: "#000000",
    title_1: "Frequently Asked",
    title_1_color: "#000000",
    title_2: "Questions",
    title_2_gradient_start_color: "#FFFFFF",
    title_2_gradient_end_color: "#02A7B6",
    section_background_color: "#FFFFFF",
    section_color: "#000000",
    backvariant: "variant_1",
    gradient_color_1: "#00D4EB",
    gradient_color_2: "#000000A8",
    gradient_color_3: "#000000A8",
    gradient_color_4: "#5345C3",
    background_color: "#000000",
    top_arrow_icon: "",
    top_arrow_icon_alt: "Expand",
    bottom_arrow_icon: "",
    bottom_arrow_icon_alt: "Collapse",
    enable_animation: false,
    layout_variant: "variant_1",
  },
  faq_3: {
    id: "",
    status: false,
    title: "FAQs",
    title_text_color: "#171719",
    title_1: "Find answers to commonly asked questions.",
    title_1_color: "#171719",
    section_background_color: "#FFFFFF",
    section_background_image: "",
    section_background_image_alt: "",
    background_color: "#130554",
    accordion_muted_index_color: "#C5C5C5",
    accordion_item_border_color: "#E0E0E0",
    accordion_answer_surface_bg: "#F8F8F8",
    accordion_answer_surface_border: "#C5C5C5",
  },
  faq_4: {
    id: "",
    status: false,
    title: "Explore More",
    title_text_color: "#171719",
    title_1: "",
    title_1_color: "#171719",
    section_background_color: "#FFFFFF",
    section_background_image: "",
    section_background_image_alt: "",
    background_color: "#130554",
    section_color: "#130554",
  },
  visionaries_1: {
    id: "",
    status: false,
    pre_title: "Led by Visionaries,",
    pre_title_color: "#FFFFFF",
    title: "Built for You",
    title_start_color: "#FFFFFF",
    title_end_color: "#02A7B6",
    title_gradient_angle: "90deg",
    desktop_background_color: "#0A0A0A",
    mobile_background_color: "#0A0A0A",
    backvariant: "variant_1",
    use_categories: true,
    gradient_color_1: "#5345C3",
    gradient_color_2: "#000000A8",
    gradient_color_3: "#000000A8",
    gradient_color_4: "#02A7B6",
    left_navigation_icon: "",
    right_navigation_icon: "",
    progress_track_color: "#444444",
    progress_fill_color: "#FFFFFF",
    enable_animation: false,
    enable_coverflow_carousel: true,
  },
  learn_from_leaders: {
    id: "",
    status: false,
    pre_title: LFL_DEFAULT_HEADING_LINE_1,
    pre_title_color: "#FFFFFF",
    title: LFL_DEFAULT_HEADING_LINE_2,
    title_start_color: "#FFFFFF",
    title_end_color: "#02A7B6",
    description: LFL_DEFAULT_DESCRIPTION,
    description_color: LFL_DEFAULT_DESCRIPTION_COLOR,
    desktop_background_color: "#000000",
    mobile_background_color: "#000000",
    enable_animation: false,
    metadata: [],
  },
  learn_from_leaders_1003: {
    id: "",
    status: false,
    title: "Learn From {Leaders}",
    title_start_color: "#171719",
    title_end_color: "#E1473F",
    description: "",
    description_color: "#171719",
    background_image: "",
    background_image_alt: "",
    cta_text: "",
    cta_color: "#E1473F",
    cta_text_color: "#FFFFFF",
    metadata: [],
  },
  program_structure_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    container_background_color: "#FFFFFF",
    cta_button_text: "Learn More",
    cta_button_text_color: "#FFFFFF",
    cta_button_color: "#BE2330",
    cta_button_icon: "",
    cta_button_icon_alt: "Learn more",
  },
  elective_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    sub_title_text_color: "#000000",
    background_color: "#FFFFFF",
  },
  program_fee_1: {
    id: "",
    title: "",
    title_text_color: "#000000",
    note: "",
    note_text_color: "#000000",
    other_fee: "",
    status: false,
    background_color: "#ffffff",
    table_header_background_color: "#f5f5f5",
    cta_button_text: "Learn More",
    cta_button_text_color: "#FFFFFF",
    cta_button_color: "#BE2330",
    cta_button_icon: "",
    cta_button_icon_alt: "Learn more",
    tabs: [],
    rows: [],
    columns: [],
    values: [],
  },
  program_fee_2: {
    id: "",
    title_1: "Investment in",
    title_1_color: "rgba(34,34,34,0.8)",
    title_2: "Your Future",
    title_2_gradient_from: "#342B7C",
    title_2_gradient_to: "#02A7B6",
    table_gradient_from: "#342b7c",
    table_gradient_to: "#02a7b6",
    metadata_title: "What You Get:",
    metadata_title_color: "#000000",
    metadata: [],
    metadata_1: [],
    categories: [],
    cta_text: "Apply Now",
    cta_text_color: "#ffffff",
    cta_gradient_from: "#02a7b6",
    cta_gradient_to: "#342b7c",
    cta_icon: "",
    cta_icon_alt: "",
    background_color: "#ffffff",
    status: false,
  },
  program_structure_3: {
    id: "",
    section_background_image: "",
    section_background_image_alt: "",
    section_overlay_rgba: "",
    headline_primary: "The Complete Curriculum -",
    headline_primary_color: "#FFFFFF",
    headline_accent: "A Structured Path to Your Program",
    headline_accent_color: "#F5C542",
    topics_column_title: "Topic Covered",
    topics_inner_heading: "Topics",
    card_background_color: "#FFFFFF",
    module_list_track_background_color: "#E8EAED",
    content_panel_background_color: "#F3F4F6",
    module_inactive_border_color: "#E5E7EB",
    module_list_divider_color: "#94A3B8",
    module_active_background_color: "#FF6B00",
    module_active_text_color: "#FFFFFF",
    module_inactive_text_color: "#111827",
    section_background_color: "#0B0F14",
    cta_button_text: "Apply Now",
    cta_button_text_color: "#FFFFFF",
    cta_button_color: "#FF6B00",
    brochure_cta_text: "Download Brochure",
    brochure_cta_text_color: "#FFFFFF",
    brochure_cta_border_color: "#FFFFFF",
    cta_button_is_cta: true,
    brochure_cta_is_cta: true,
    metadata: [],
    status: false,
  },
  program_structure_4: {
    id: "",
    section_background_image: "",
    section_background_image_alt: "",
    section_overlay_rgba: "rgba(0,0,0,0.55)",
    headline_primary: "Program Structure",
    headline_primary_color: "#FFFFFF",
    headline_accent: "",
    headline_accent_color: "#F5C542",
    topics_column_title: "",
    card_background_color: "#FFFFFF",
    module_list_track_background_color: "#E8EAED",
    content_panel_background_color: "#F3F4F6",
    module_inactive_border_color: "#E5E7EB",
    module_list_divider_color: "#94A3B8",
    module_active_background_color: "#FF6B00",
    module_active_text_color: "#FFFFFF",
    module_inactive_text_color: "#111827",
    section_background_color: "#0B0F14",
    cta_button_text: "Apply Now",
    cta_button_text_color: "#FFFFFF",
    cta_button_color: "#FF6B00",
    brochure_cta_text: "Download Brochure",
    brochure_cta_text_color: "#FFFFFF",
    brochure_cta_border_color: "#FFFFFF",
    cta_button_is_cta: true,
    brochure_cta_is_cta: true,
    metadata: [],
    status: false,
  },
  program_structure_5: {
    id: "",
    section_background_image: "",
    section_background_image_alt: "",
    section_overlay_rgba: "",
    section_background_color: "#0B0F14",
    headline_primary: "Skills You'll {Master}",
    headline_primary_color: "#FFFFFF",
    headline_accent_color: "#F5C542",
    section_description: "",
    metadata: [],
    status: false,
  },
  program_structure_2: {
    id: "",
    layout_variant: "variant_1",
    title_1: "Learn What the ",
    title_1_color: "rgba(255,255,255,0.8)",
    title_2: "Industry Actually Uses",
    title_2_gradient_start_color: "#FFFFFF",
    title_2_gradient_end_color: "#02a7b6",
    description: "",
    description_color: "rgba(255,255,255,0.7)",
    credits_text: "Total Credits:",
    credits_text_color: "#FFFFFF",
    total_credits: "125",
    total_credits_color: "#0febff",
    table_heading_1: "Type",
    table_heading_1_color: "#FFFFFF",
    table_heading_2: "Subjects",
    table_heading_2_color: "#FFFFFF",
    table_heading_3: "Credits",
    table_heading_3_color: "#FFFFFF",
    table_header_background_color: "#9f1c34",
    table_total_text: "Total",
    table_total_text_color: "#FFFFFF",
    note: "",
    note_color: "#FFFFFF",
    cta_button_text: "Download Brochure",
    cta_button_text_color: "#222222",
    cta_button_background_color: "#FFFFFF",
    cta_button_icon: "",
    cta_button_icon_alt: "Download Brochure",
    cta_button_shadow_enabled: true,
    cta_button_shadow_color: "rgba(202,125,139,0.6)",
    tools_title: "Tools You'll Master",
    tools_title_color: "#4cecfa",
    skills_title: "Skills You'll Acquire",
    skills_title_color: "#4cecfa",
    background_color: "#000000",
    backvariant: "variant_1",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    semester_active_bg_color: "#02a7b6",
    semester_active_text_color: "#FFFFFF",
    semester_inactive_border_color: "rgba(255,255,255,0.4)",
    semester_inactive_text_color: "#FFFFFF",
    status: false,
    semesters: [],
    subjects: [],
    tools: [],
    skills: [],
  },
  recruiters_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    cta_button_text: "Learn More",
    cta_button_text_color: "#FFFFFF",
    cta_button_color: "#BE2330",
    cta_button_icon: "",
    cta_button_icon_alt: "Learn more",
    metadata: [],
  },
  eligibility_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    description_text_color: "#000000",
    background_color: "#FFFFFF",
    button_text: "Read more",
    button_text_color: "#000000",
    button_color: "#FFFFFF",
    button_icon: "",
    button_icon_alt: "Read more",
    read_more_text: "Read More",
    read_more_color: "#BE2330",
  },
  eligibility_2: {
    id: "",
    status: false,
    title: "Eligibility Criteria",
    title_text_color: "#F8F9FA",
    background_gradient_start_color: "#0A3D5B",
    background_gradient_end_color: "#0A3D5B",
    background_gradient_angle: "180deg",
    category_border_color: "#FFFFFF",
    category_active_text_color: "#0A3D5B",
    category_active_background_color: "#FFFFFF",
    category_inactive_text_color: "#F8F9FA",
    content_text_color: "#F8F9FA",
    button_text: "Apply Now",
    button_text_color: "#222222",
    button_color: "#FFFFFF",
    read_more_url: "",
    categories: [],
  },
  specialisation_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    cta_text: "Load More",
    cta_text_color: "#000000",
    cta_background_color: "#FFFFFF",
    cta_icon: "",
    cta_icon_alt: "Load more",
    background_color: "#FFFFFF",
    metadata: [],
  },

  specialisation_5017: {
    id: "",
    status: false,
    title_text_color: "#000000",
    cta_text: "Load More",
    cta_text_color: "#000000",
    cta_background_color: "#FFFFFF",
    cta_icon: "",
    cta_icon_alt: "Load more",
    background_color: "#FFFFFF",
    metadata: [],
  },
  career_prospects_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    sub_title_text_color: "#000000",
    cta_text: "Read More",
    cta_text_color: "#000000",
    cta_background_color: "#FFFFFF",
    cta_icon: "",
    cta_icon_alt: "Read more",
    background_color: "#FFFFFF",
    metadata: [],
  },
  career_prospects_2: {
    id: "",
    status: true,
    title: "Beyond a Certificate – Built for Career Impact",
    title_text_color: "#000000",
    sub_title: "Earn recognised credentials and academic recognition.",
    sub_title_text_color: "#000000",
    note_text:
      "Top 10 percentile performers in each cohort receive a Certificate with Distinction, recognised on the credential itself.",
    note_text_color: "#000000",
    background_color: "#FFFFFF",
    certificate_image: "",
    certificate_image_alt: "Certificate",
    metadata: [],
  },
  career_prospects_3: {
    id: "",
    status: true,
    title: "Probable",
    title_text_color: "#000000",
    sub_title: "Tools &\nFrameworks",
    sub_title_text_color: "#D35400",
    background_color: "#FFFFFF",
    certificate_image: "",
    certificate_image_alt: "Tools and frameworks",
    metadata: [],
  },
  career_prospects_4: {
    id: "",
    status: true,
    title: "Probable {Tools & Frameworks}",
    title_text_color: "#000000",
    title_highlight_color: "#DB5803",
    background_color: "#FFFFFF",
    background_image: "",
    certificate_image: "",
    certificate_image_mobile: "",
    certificate_image_alt: "Tools and frameworks",
    metadata: [],
  },
  certification_1: {
    id: "",
    layout_variant: "variant_1",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    pre_title: "Graduate with globally recognized ",
    pre_title_color: "#222222CC",
    title: "certifications alongside your MBA.",
    title_start_color: "#FFFFFF",
    title_end_color: "#00D4EB",
    title_gradient_direction: "90deg",
    apply_now_text: "Apply Now",
    apply_now_button_start_color: "#FFFFFF",
    apply_now_button_end_color: "#00D4EB",
    button_gradient_direction: "90deg",
    enable_button_shadow: false,
    apply_now_button_icon: "",
    apply_now_button_icon_alt: "",
    left_navigation_icon: "",
    alt_text_left_nav_icon: "",
    right_navigation_icon: "",
    alt_text_right_nav_icon: "",
    scroll_frequency: "",
    backvariant: "variant_1",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    metadata: [],
  },
  application_process_1: {
    id: "",
    status: false,
    layout_variant: "variant_1",
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    gradient_color_1: "#00D4EB54",
    gradient_color_2: "#000000A8",
    gradient_color_3: "#000000A8",
    gradient_color_4: "#5345C3",
    pre_title: "",
    pre_title_color: "#222222CC",
    title: "",
    title_start_color: "",
    title_end_color: "",
    title_gradient_direction: "",
    subtitle_1: "",
    subtitle_1_color: "#FFFFFF",
    subtitle_1_background_color: "#56E2F54D",
    subtitle_2: "",
    subtitle_2_color: "#FFFFFF",
    subtitle_2_background_color: "#56E2F54D",
    background_image: "",
    alt_background_image: "",
    box_start_color: "",
    box_end_color: "",
    gradient_direction: "",
    metadata_1: [],
    metadata_2: [],
  },
  application_process_2: {
    id: "",
    status: true,
    background_image: "",
    alt_background_image: "",
    title: "Our Admission process",
    title_start_color: "#FFFFFF",
    subtitle_1:
      "Complete your application and enrol in the program with four easy steps!",
    subtitle_1_color: "#F3F4F6",
    cta_label: "Apply Now",
    accent_color: "#FF6B00",
    cta_link: "",
    cta_is_cta: true,
    metadata_1: [],
    metadata_2: [],
  },
  skills_1: {
    id: "",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    variant: "1",
    pre_title: "",
    pre_title_color: "#FFFFFF",
    title: "",
    title_color: "#ca6363",
    description: "",
    description_color: "#FFFFFF",
    metadata: [],
  },
  comparison_1: {
    id: "",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    pre_title: "Why Online ",
    pre_title_color: "#FFFFFF",
    title: "MBA Stands Out",
    title_start_color: "#ca6363",
    title_end_color: "",
    title_gradient_angle: "90deg",
    description: "We've reimagined online business education. Here's how we're different from typical programs.",
    description_text_color: "#FFFFFFB2",
    column1: "Online MBA",
    column1_text_color: "#000000",
    column1_background_color: "#02A7B6",
    column2: "Traditional Online MBA",
    column2_text_color: "#FFFFFF",
    column2_background_color: "#000000",
    column3: "Standalone CFP® Programs",
    column3_text_color: "#FFFFFF",
    column3_background_color: "#000000",
    cta_text: "Apply Now",
    cta_text_color: "#FFFFFF",
    cta_background_color: "#02A7B6",
    variant: "1",
    metadata: [],
  },
  comparison_1004: {
    id: "",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    pre_title: "CU Online",
    pre_title_color: "#E1473F",
    title: "Traditional Learning",
    title_start_color: "#000000",
    description: "",
    description_text_color: "#000000",
    column1: "CU Online",
    column1_text_color: "#FFFFFF",
    column1_background_color: "#E1473F",
    column2: "Traditional Learning",
    column2_text_color: "#000000",
    column2_background_color: "#FFFFFF",
    variant: "1004",
    metadata: [],
  },
  leaders_1: {
    id: "",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    pre_title: "Graduated builds carrer across",
    pre_title_color: "#FFFFFF",
    title: "certifications alongside your MBA.",
    title_start_color: "#ca6363",
    title_end_color: "",
    title_gradient_angle: "90deg",
    scroll_frequency: 5,
    description_box_start_color: "",
    description_box_end_color: "",
    description_box_gradient_angle: "",
    description_section_pre_title: "",
    description_section_pre_title_color: "#FFFFFF",
    description_section_title: "Learn from leaders managing billions in client wealth",
    description_section_title_color: "#FFFFFF",
    description_section_description: "",
    description_section_description_text_color: "#FFFFFF",
    variant: "1",
    metadata: [],
  },
  who_is_this_program_for_1: {
    id: "",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    gradient_color_1: "#00D4EB54",
    gradient_color_2: "#000000A8",
    gradient_color_3: "#000000A8",
    gradient_color_4: "#5345C3",
    pre_title: "Welcome to",
    pre_title_color: "#FFFFFFCC",
    title: "Master of Business Administration",
    title_start_color: "#FFFFFF",
    title_end_color: "#00D4EB",
    title_gradient_angle: "90deg",
    description: "skills description",
    description_color: "#FFFFFF",
    cta_color: "#FFFFFF",
    cta_text: "Apply Now",
    cta_text_color: "#222222",
    metadata: [],
  },
  banner_video_1: {
    id: "",
    status: true,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    slide_frequency: 5000,
    metadata: [],
  },
  walk_through_1: {
    id: "",
    status: false,
    desktop_bg_image: "",
    mobile_bg_image: "",
    alt_image: "",
    title_prefix:
      "IIT Kharagpur marks 75 Years of Academic Excellence and Leadership",
    title_highlight: "",
    title_suffix: "",
    title_highlight_color: "#FFFFFF",
    video_link: "",
    cta_label: "View Video",
    overlay_color: "#352D59",
    overlay_opacity: 0.9,
  },
  life_milestone_1: {
    id: "",
    status: false,
    title_prefix: "Life after ",
    title_highlight: "IIT Kharagpur",
    title_suffix: "",
    title_highlight_color: "#FFC600",
    background_color: "#291973",
    image_frame_color: "#E1473F",
    hero_image: "",
    hero_image_alt: "",
    timeline_color: "#807070",
    auto_rotate_seconds: 0,
    metadata: [],
  },
  specialisation_carousel_3000: {
    id: "",
    status: false,
    design_variant: "variant_1",
    title: "Choose Your Online MBA Programme Specialisation",
    title_color: "#101112",
    subtitle: "Industry-aligned curriculum, at par with on-campus programs.",
    subtitle_color: "#101112",
    background_color: "#FFFFFF",
    card_background_color: "#FFFFFF",
    card_title_color: "#000000",
    card_arrow_color: "#292D32",
    card_icon_color: "#292D32",
    show_navigation: true,
    nav_arrow_color: "#9F1C34",
    nav_prev_icon: "",
    nav_prev_icon_alt: "",
    nav_next_icon: "",
    nav_next_icon_alt: "",
    show_pagination: true,
    scroll_active_color: "#9F1C34",
    scroll_inactive_color: "#D9D9D9",
    scrollbar_thumb_color: "#9F1C34",
    scrollbar_track_color: "#F1F1F1",
    show_scrollbar: false,
    show_cta: true,
    cta_text: "Apply Now",
    cta_text_color: "#FFFFFF",
    cta_background_color: "#9F1C34",
    cta_shadow_color: "rgba(202,125,139,0.6)",
    cta_icon: "",
    cta_icon_alt: "",
    cta_url: "",
    modal_header_background_color: "#F5E4E7",
    modal_title_color: "#101112",
    modal_body_background_color: "#FFFFFF",
    modal_heading_color: "#9F1C34",
    modal_text_color: "#000000",
    modal_close_icon_color: "#9F1C34",
    modal_close_icon: "",
    modal_close_icon_alt: "",
    modal_primary_cta_text: "Apply Now",
    modal_primary_cta_text_color: "#FFFFFF",
    modal_primary_cta_background_color: "#9F1C34",
    modal_primary_cta_icon: "",
    modal_primary_cta_icon_alt: "",
    modal_brochure_cta_text: "Download Brochure",
    modal_brochure_cta_text_color: "#000000",
    modal_brochure_cta_background_color: "#FFFFFF",
    modal_brochure_cta_border_color: "#000000",
    modal_brochure_cta_icon: "",
    modal_brochure_cta_icon_alt: "",
    metadata: [],
  },
  life_milestone_2: {
    id: "",
    status: false,
    background_color: "#291973",
    block_main_title: "",
    block_subtitle: "",
    block_body: "",
    block_cta_label: "",
    block_cta_url: "",
    block_feature_label: "",
    hero_image: "",
    hero_image_alt: "",
  },
  life_milestone_3: {
    id: "",
    status: false,
    background_color: "#291973",
    block_main_title: "",
    block_subtitle: "",
    block_body: "",
    block_cta_label: "",
    block_cta_url: "",
    is_cta: true,
    hero_image: "",
    hero_image_mobile: "",
    hero_image_alt: "",
    metadata: [],
  },
  enrollments_1: {
    id: "",
    status: false,
    headline: "Ready to Take the Next Step? Enroll Today!",
    cta_label: "Apply Now",
    cta_url: "",
    open_in_new_tab: true,
    outer_background_color: "#FDE1D1",
    inner_background_color: "#DB5803",
    headline_color: "#FFFFFF",
    cta_background_color: "#FFFFFF",
    cta_text_color: "#DB5803",
  },
  enrollments_2: {
    id: "",
    status: false,
    headline_primary: "A full-stack AI engineering program",
    headline_accent: "to ship real-world AI systems",
    body_paragraph_1: "",
    body_paragraph_2: "",
    body_paragraph_3: "",
    stat_1_value: "",
    stat_1_label: "",
    stat_2_value: "",
    stat_2_label: "",
    enable_lead_popup: true,
    enable_lead_form: false,
    bound_course_id: "",
  },
  enrollments_3: {
    id: "",
    status: false,
    title: "Why Pursue {this Programme}",
    description: "Take the next step in your career",
    cta_label: "Apply Now",
    cta_url: "",
    open_in_new_tab: true,
    desktop_image: "",
    mobile_image: "",
    background_color: "#FFFFFF",
    overlay_color: "#000000",
    overlay_opacity: "0.5",
    title_color: "#FFFFFF",
    title_highlight_color: "#FFD700",
    description_color: "#FFFFFF",
    cta_background_color: "#FFFFFF",
    cta_text_color: "#DB5803",
    enable_lead_popup: true,
  },
  enrollments_4: {
    id: "",
    status: false,
    title: "Key {advantages} of this programme",
    cta_label: "Get Started",
    cta_url: "",
    open_in_new_tab: true,
    desktop_image: "",
    mobile_image: "",
    background_color: "#FFFFFF",
    title_color: "#0A0A0A",
    title_highlight_color: "#DB5803",
    cta_background_color: "#DB5803",
    cta_text_color: "#FFFFFF",
    enable_lead_popup: true,
    metadata: [],
  },
  enrollments_5: {
    id: "",
    status: false,
    title: "About this {Programme}",
    description: "",
    cta_label: "Apply Now",
    cta_url: "",
    open_in_new_tab: true,
    background_color: "#FFFFFF",
    title_color: "#0A0A0A",
    title_highlight_color: "#DB5803",
    description_color: "#0A0A0A",
    body_text_color: "#0A0A0A",
    accent_color: "#DB5803",
    cta_background_color: "#DB5803",
    cta_text_color: "#FFFFFF",
    enable_lead_popup: true,
    metadata: [],
  },
  success_stories_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    left_navigation_icon: "",
    left_navigation_icon_alt: "Left navigation",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Right navigation",
    background_color: "#FFFFFF",
    metadata: [],
  },
  success_stories_2: {
    id: "",
    status: true,
    title: "ATLAS Student Join Industry Leaders",
    title_text_color: "#DDE567",
    subtitle:
      "Career growth, industry opportunities, and professional success stories from the ATLAS ecosystem",
    subtitle_text_color: "#FFFFFF",
    left_navigation_icon: "",
    left_navigation_icon_alt: "Previous",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Next",
    indicator_color: "#DDE567",
    background_color: "#081F3D",
    metadata: [],
  },
  what_makes_course_different_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    sub_title_text_color: "#000000",
    point_1_text_color: "#000000",
    point_1_icon: "",
    point_1_icon_alt: "Point 1",
    point_2_text_color: "#000000",
    point_2_icon: "",
    point_2_icon_alt: "Point 2",
    cta_text_color: "#000000",
    cta_background_color: "#FFFFFF",
    cta_icon: "",
    cta_icon_alt: "Read more",
    cta_shadow_enabled: false,
    background_color: "#FFFFFF",
    carousel_button_color: "#f74958",
    metadata: [],
  },
  what_makes_course_different_2: {
    id: "",
    status: false,
    desktop_background_color: "#000000",
    mobile_background_color: "#000000",
    backvariant: "variant_1",
    gradient_color_1: "#5345C3",
    gradient_color_2: "#000000",
    gradient_color_3: "#000000",
    gradient_color_4: "#00D4EB",
    pre_title: "Why Online ",
    pre_title_color: "#FFFFFFCC",
    title: "MBA Stands Out",
    title_start_color: "#FFFFFF",
    title_end_color: "#02A7B6",
    title_gradient_angle: "90deg",
    description:
      "We've reimagined online business education. Here's how we're different from typical programs.",
    description_text_color: "#FFFFFFB2",
    right_navigation_icon: "",
    alt_right_nav_icon: "icon 1",
    left_navigation_icon: "",
    alt_left_icon: "icon2",
    cta_text: "Apply Now",
    cta_text_color: "#FFFFFF",
    cta_start_color: "#342B7C",
    cta_end_color: "#02A7B6",
    cta_gradient_angle: "90deg",
    carousel_button_color: "#f74958",
    metadata: [],
  },
  vice_chancellor_1: {
    id: "",
    status: false,
    metadata: [],
  },
  faculty_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    left_navigation_icon: "",
    left_navigation_icon_alt: "Left navigation",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Right navigation",
    description: "",
    description_text_color: "#000000",
    description_background_color: "#ffffff",
    metadata: [],
  },
  faculty_2: {
    id: "",
    status: false,
    title_1: "Led by Visionaries,",
    title_1_color: "rgba(255, 255, 255, 0.8)",
    title_2: "Built for You",
    title_2_gradient_start_color: "#FFFFFF",
    title_2_gradient_end_color: "#02A7B6",
    title_2_gradient_angle: "90deg",
    left_navigation_icon: "",
    left_navigation_icon_alt: "Left navigation",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Right navigation",
    range_color: "#FFFFFF",
    cta_button_color: "#FFFFFF",
    cta_text_color: "#222222",
    cta_icon: "",
    cta_icon_alt: "CTA icon",
    auto_scroll_frequency: 5000,
    backvariant: "variant_1",
    layout_variant: "variant_1",
    gradient_color_1: "#00D4EB",
    gradient_color_2: "#000000",
    gradient_color_3: "#000000",
    gradient_color_4: "#5345C3",
    background_color: "#000000",
    metadata: [],
  },
  faculty_3: {
    id: "",
    status: false,
    title: "Learning Led by ",
    title_text_color: "#000000",
    title_1: "Distinguished Faculty",
    title_1_color: "#E36E26",
    description: "India's Strongest Academic Bench in AI & Data Science",
    description_text_color: "#000000",
    description_background_color: "#FFFFFF",
    metadata: [],
  },
  faculty_4: {
    id: "",
    status: false,
    title: "Learning Led by {Distinguished Faculty}",
    title_text_color: "#FFFFFF",
    title_1_color: "#FFD700",
    background_color: "#291973",
    cta_button_color: "#E36E26",
    cta_text_color: "#FFFFFF",
    metadata: [],
  },
  faculty_5: {
    id: "",
    status: false,
    title: "Meet Our {Leadership}",
    title_text_color: "#1A1A1A",
    title_1_color: "#E36E26",
    description: "",
    description_text_color: "#1A1A1A",
    background_color: "#FFFFFF",
    background_image: "",
    background_image_alt: "",
    metadata: [],
  },
  faculty_6: {
    id: "",
    status: false,
    title: "What Our {Students Say}",
    title_text_color: "#1A1A1A",
    title_1_color: "#E1473F",
    description: "",
    description_text_color: "#1A1A1A",
    background_color: "#FFFFFF",
    metadata: [],
  },
  testimonials_1: {
    id: "",
    status: false,
    title_text_color: "#000000",
    card_color: "#ffffff",
    left_navigation_icon: "",
    left_navigation_icon_alt: "Left navigation",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Right navigation",
    metadata: [],
  },
  testimonials_2: {
    id: "",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    pre_title: "",
    pre_title_color: "#FFFFFF",
    title: "",
    title_start_color: "#ca6363",
    title_end_color: "",
    title_gradient_angle: "90deg",
    scroll_frequency: null,
    left_navigation_icon: "",
    left_navigation_icon_alt: "Previous",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Next",
    button_text: "Apply Now",
    button_text_color: "#222222",
    button_color: "#FFFFFF",
    enable_animation: false,
    metadata: [],
    layout_variant: "variant_1",
    button_icon: "",
  },
  masterclass_1: {
    id: "",
    layout_variant: "variant_1",
    status: false,
    title_text_color: "#000000",
    background_image: "",
    left_navigation_icon: "",
    left_navigation_icon_alt: "Left navigation",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Right navigation",
    button_text: "",
    button_background_color: "#BE2330",
    button_text_color: "#FFFFFF",
    button_icon: "",
    button_icon_alt: "Button icon",
    button_hover_icon: "",
    button_hover_icon_alt: "Button hover icon",
    show_button: true,
    show_quote_icon: true,
    quote_icon: "",
    quote_icon_alt: "Quote icon",
    metadata: [],
  },
  placements_1: {
    id: "",
    status: false,
    title_color: "#000000",
    background_color: "#ffffff",
    metadata: [],
  },
  placements_2: {
    id: "",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    title: "certifications alongside your MBA.",
    title_color: "#ca6363",
    button_text: "Apply Now",
    button_text_color: "#FFFFFF",
    button_color: "#222222",
    scroll_frequency: "3000",
    metadata: [],
  },
  placements_3: {
    id: "",
    status: false,
    background_color: "#000000",
    row1_image: "",
    row2_image: "",
    row1_scroll_frequency: 3000,
    row2_scroll_frequency: 3000,
    row1_scroll_direction: "left",
    row2_scroll_direction: "right",
  },
  placements_4: {
    id: "",
    status: false,
    desktop_background_color: "#000000",
    mobile_background_color: "#000000",
    backvariant: "variant_3",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    rows_config: [],
  },
  mobile_app_1: {
    id: "",
    status: false,
    is_blog: false,
    background_color: "#000000",
    title_text_color: "#000000",
    google_play_icon: "",
    google_play_icon_alt: "Get it on Google Play",
    google_play_link: "",
    app_store_icon: "",
    app_store_icon_alt: "Download on the App Store",
    app_store_link: "",
  },
  footer_1: {
    id: "",
    status: false,
    title: "",
    title_text_color: "#000000",
    enable_footer_content: true,
    background_color: "#ffffff",
    text_color: "#000000",
    enable_copyright: true,
    enable_animation: false,
    copyright_text: "",
    copyright_text_color: "#000000",
    variant: "1",
    categories: [],
  },
  footer_2: {
    id: "",
    status: false,
    gradient_start_color: "#3C398E",
    gradient_end_color: "#00BFFF",
    copyright_text: "Copyright 2026 of [University Name]",
    variant: "2",
  },
  footer_3: {
    id: "",
    status: false,
    title: "",
    title_text_color: "#000000",
    enable_footer_content: true,
    background_color: "#ffffff",
    text_color: "#000000",
    enable_copyright: true,
    copyright_text: "",
    copyright_text_color: "#000000",
    enable_animation: false,
    variant: "3",
    categories: [],
    column_count: 1,
  },
  subscribe_1: {
    id: "",
    status: false,
    background_color: "#EF5A6F",
    title: "",
    title_color: "#FFFFFF",
    description: "",
    description_color: "#FFFFFF",
    placeholder_text: "",
    button_name: "",
    button_background_color: "#BE2330",
    button_text_color: "#FFFFFF",
    button_icon: "",
    button_icon_alt: "Subscribe",
    success_icon: "",
    success_icon_alt: "Success",
    success_text: "",
    success_text_color: "#FFFFFF",
  },
  program_cards_1: {
    id: "",
    status: false,
    title: "",
    title_text_color: "#000000",
    card_background_color: "#ffffff",
    load_more_text: "",
    load_more_desktop_icon: "",
    load_more_desktop_icon_alt: "",
    load_more_mobile_icon: "",
    load_more_mobile_icon_alt: "",
    load_more_desktop_text_color: "#000000",
    load_more_mobile_text_color: "#000000",
    // load_more_desktop_background_color: "#ffffff",
    load_more_mobile_background_color: "#ffffff",
    metadata: [],
  },
  program_cards_2: {
    id: "",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    font_family_variant: "variant_2",
    gradient_color_1: "rgba(0, 212, 235, 0.33)",
    gradient_color_2: "rgba(0, 0, 0, 0.66)",
    gradient_color_3: "rgba(0, 0, 0, 0.66)",
    gradient_color_4: "rgba(83, 69, 195, 0.46)",
    pre_title: "Why Online ",
    pre_title_color: "#FFFFFF",
    title: "MBA Stands Out",
    title_start_color: "#ca6363",
    title_end_color: "",
    title_gradient_angle: "90deg",
    cta_text: "View All courses",
    cta_text_color: "#FFFFFF",
    cta_background_start_color: "#02A7B6",
    cta_background_end_color: "#342B7C",
    cta_background_angle: "90deg",
    cta_url: "",
    cta_icon: "",
    cta_icon_alt: "",
    cta_button_variant: "variant_2",
    title_1: "",
    title_1_color: "#000000",
    title_2: "",
    title_2_gradient_start_color: "#FFFFFF",
    title_2_gradient_end_color: "#02A7B6",
    explore_course_text: "Explore Course",
    explore_course_text_color: "#02A7B6",
    explore_course_border_color: "#02A7B6",
    explore_all_courses_text: "View All Course",
    modal_cta_text: "Apply Now",
    show_features: false,
    enable_animation: false,
    use_categories: true,
    metadata: [],
    categories: [],
  } as IProgramCards,
  program_cards_5: {
    id: "",
    status: false,
    title: "",
    title_text_color: "#000000",
    card_background_color: "#ffffff",
    load_more_text: "",
    load_more_desktop_icon: "",
    load_more_desktop_icon_alt: "",
    load_more_mobile_icon: "",
    load_more_mobile_icon_alt: "",
    load_more_desktop_text_color: "#000000",
    load_more_mobile_text_color: "#000000",
    load_more_mobile_background_color: "#ffffff",
    metadata: [],
  } as IProgramCards,
  program_cards_3: {
    id: "",
    status: false,
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    pre_title: "Explore Our",
    pre_title_color: "#171719",
    title: "Online Programs",
    title_start_color: "#E1473F",
    use_categories: true,
    card_background_color: "#FFFFFF",
    card_hover_background_color: "#E1473F",
    card_hover_text_color: "#FFFFFF",
    cta_text: "View More",
    cta_text_color: "#FFFFFF",
    cta_background_start_color: "#E1473F",
    cta_icon: "",
    cta_icon_alt: "",
    metadata: [],
    categories: [],
  },
  program_cards_4: {
    id: "",
    status: false,
    title: "Careers After {This Course}",
    title_text_color: "#000000",
    title_highlight_color: "#E1473F",
    salary_text_color: "#E1473F",
    key_skills_text_color: "#171719",
    key_skills_background_color: "#F3F4F6",
    key_skills_border_color: "#FF9944",
    description: "",
    note: "",
    background_image: "",
    background_image_alt: "",
    metadata: [],
  },
  thank_you_1: {
    id: "",
    desktop_bg_image: "",
    mobile_bg_image: "",
    alt_image: "Thank you background",
    gradient_start_color: "rgba(0, 0, 0, 0.3)",
    gradient_end_color: "rgba(0, 0, 0, 0.7)",
    description: "",
  },
  thank_you_1009: {
    id: "",
    logo_image: "",
    logo_alt: "Logo",
    desktop_bg_image: "",
    mobile_bg_image: "",
    alt_image: "Thank you background",
    description: "",
    referral_description: "",
    influencer_description: "",
  },
  thank_you_1073: {
    id: "",
    desktop_bg_image: "",
    mobile_bg_image: "",
    side_image: "",
    alt_image: "Thank you background",
    description: "",
    referral_description: "",
    influencer_description: "",
  },
  rich_text_editor_1: {
    id: "",
    content: "",
    article_band_background_color: "",
    background_color: "#FFFFFF",
    enable_padding: true,
    padding_x: "",
    padding_y: "",
    status: false,
  },
  blog_with_lead_1: {
    id: "",
    content: "",
    article_band_background_color: "",
    background_color: "#FFFFFF",
    enable_padding: true,
    enable_lead_form: true,
    status: false,
  },
  latest_blogs_1: {
    id: "",
    ...LATEST_BLOGS_1_WIDGET_DEFAULTS,
  },
  latest_blogs_2: {
    id: "",
    ...LATEST_BLOGS_2_WIDGET_DEFAULTS,
  },
  popular_blogs_1: {
    id: "",
    ...POPULAR_BLOGS_1_WIDGET_DEFAULTS,
  },
  blog_list_1: {
    id: "",
    title: "Blogs",
    title_color: "#000000",
    description: "",
    description_color: "#555555",
    section_background_color: "#FFFFFF",
    category_title_color: "#374151",
    category_active_background_color: "#BE2330",
    category_carousel_left_arrow_url: "",
    category_carousel_right_arrow_url: "",
    date_calendar_icon_url: "",
    date_text_color: "#373737",
    date_icon_color: "#373737",
    card_title_color: "#101112",
    card_description_color: "#101112",
    read_more_label: "Read More",
    read_more_text_color: "#60050e",
    status: false,
  },
  counselor_call_cta_1: {
    id: "",
    title: "Talk to our academic counsellor today!",
    title_text_color: "#000000",
    section_background_color: "#F4F4F4",
    phone_placeholder: "Enter your phone number",
    pill_background_color: "#FFFFFF",
    pill_border_color: "#E5E5E5",
    button_label: "Call Counsellor",
    button_background_color: "#BE2330",
    button_text_color: "#FFFFFF",
    counselor_tel: "",
    subtitle: "Know the right career fit for you",
    subtitle_text_color: "#000000",
    status: false,
  },
  breadcrumb_banner_1: {
    id: "",
    breadcrumb_strip_background_color: "#F4F4F4",
    link_text_color: "#666666",
    current_text_color: "#943126",
    separator_color: "#737373",
    metadata: [],
    status: false,
  },
  blog_inline_image_1: {
    id: "",
    image_url: "",
    mobile_image_url: "",
    alt_text: "",
    caption: "",
    caption_text_color: "#737373",
    caption_alignment: "left",
    border_radius_px: 8,
    status: false,
  },
  para_text_1: {
    id: "",
    description: "",
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_3",
    gradient_color_1: "#00D4EB",
    gradient_color_2: "#000000",
    gradient_color_3: "#000000",
    gradient_color_4: "#5345C3",
    text_color: "#333333",
    status: false,
    enable_animation: false,
  },
  author_profile_3000: {
    id: "",
    author_id: "",
    font_family: "var(--font-poppins), Poppins, sans-serif",
    background_color: "#FFFFFF",
    use_gradient_background: true,
    gradient_angle: "34deg",
    gradient_start_color: "rgba(95, 78, 226, 0.1)",
    gradient_end_color: "rgba(0, 212, 235, 0.1)",
    show_breadcrumb: true,
    metadata: [],
    breadcrumb_text_color: "rgba(0,0,0,0.6)",
    breadcrumb_active_color: "#342B7C",
    card_background_color: "#FFFFFF",
    card_border_color: "rgba(0,0,0,0.1)",
    author_name_color: "#342B7C",
    author_role_color: "rgba(0,0,0,0.6)",
    badge_text_color: "rgba(0,0,0,0.6)",
    badge_border_color: "rgba(0,0,0,0.2)",
    badge_background_color: "#FFFFFF",
    top_contributor_label: "Top Contributor",
    top_contributor_icon_url: "",
    author_of_month_label: "Author of the Month",
    author_of_month_icon_url: "",
    associated_prefix_label: "Associated with upGrad since",
    associated_label_color: "rgba(0,0,0,0.5)",
    associated_value_color: "#000000",
    divider_color: "rgba(0,0,0,0.1)",
    domain_label: "Domain:",
    current_role_label: "Current role in the industry :",
    education_label: "Education Qualification :",
    expertise_label: "Expertise :",
    tools_label: "Tools & Technologies :",
    certifications_label: "Certifications :",
    articles_label: "of articles published",
    articles_count_suffix: "+",
    chip_background_color: "rgba(95, 78, 226, 0.1)",
    chip_text_color: "#000000",
    info_label_color: "rgba(0,0,0,0.6)",
    info_value_color: "#000000",
    about_title: "About",
    about_title_color: "#342B7C",
    about_description_color: "rgba(0,0,0,0.8)",
    follow_label: "Follow me on :",
    follow_label_color: "#000000",
    social_icon_background_color: "#FFFFFF",
    linkedin_icon_url: "",
    quora_icon_url: "",
    twitter_icon_url: "",
    blog_icon_url: "",
    show_blog_list: true,
    blog_list_background_color: "#FFFFFF",
    published_tab_label: "Published",
    popular_tab_label: "Most Popular",
    tab_text_color: "rgba(0,0,0,0.6)",
    tab_active_text_color: "#342B7C",
    tab_indicator_color: "#342B7C",
    blog_card_background_color: "#FFFFFF",
    blog_card_border_color: "rgba(0,0,0,0.1)",
    blog_card_title_color: "#101112",
    blog_card_date_color: "rgba(0,0,0,0.6)",
    blog_card_date_icon_url: "",
    read_more_label: "Read More",
    read_more_text_color: "#342B7C",
    load_more_label: "Load More",
    load_more_text_color: "#342B7C",
    load_more_background_color: "#FFFFFF",
    load_more_border_color: "#342B7C",
    status: true,
  },
  author_card_3000: {
    id: "",
    background_color: "#FFFFFF",
    author_name_color: "#342B7C",
    meta_text_color: "rgba(0,0,0,0.6)",
    description_text_color: "#000000",
    show_author_link: true,
    status: true,
  },
  author_byline_3000: {
    id: "",
    background_color: "transparent",
    byline_text_color: "#000000",
    meta_text_color: "rgba(0,0,0,0.6)",
    show_author_link: true,
    show_share: true,
    share_label_color: "#000000",
    share_icon_background_color: "#FFFFFF",
    show_copy_link: true,
    show_whatsapp: true,
    show_linkedin: true,
    status: true,
  },
  header_5001: {
    id: "",
    status: true,
    background_color: "#FFFFFF",
    show_bar_shadow: false,
    sticky_to_top: true,
    show_on_mobile: true,
    show_logo: true,
    logo_image: "",
    logo_alt_text: "",
    phone_number: "",
    phone_text_color: "#272323",
    phone_icon: "",
    phone_icon_alt: "",
    whatsapp_show: true,
    whatsapp_number: "",
    whatsapp_link: "",
    whatsapp_button_text: "WhatsApp Chat",
    whatsapp_button_color: "#4CAF51",
    whatsapp_button_text_color: "#FFFFFF",
    whatsapp_icon: "",
    apply_button_text: "Apply",
    button_color: "#BE2330",
    button_text_color: "#FFFFFF",
  },
  header_5000: {
    id: "",
    featured_pill_text: "Admissions closing soon",
    featured_pill_text_color: "#222023",
    featured_pill_bg_color: "#F4F217",
    featured_pill_border_color: "#F4F217",
    featured_pill_sticky: true,
    featured_pill_sticky_offset: "72",
    featured_pill_enable_animation: true,
    featured_pill_link_url: "",
    status: true,
  },
  heading_1: {
    id: "",
    content: "",
    heading_tag: "h2",
    font_family: "var(--font-poppins), Poppins, sans-serif",
    desktop_font_size: "32",
    mobile_font_size: "24",
    font_weight: "600",
    line_height: "1.2",
    text_color: "#111111",
    background_color: "#FFFFFF",
    word_break: true,
    status: true,
  },
  internship_heading_7000: {
    id: "",
    content: "",
    text_color: "#9F1C33",
    background_color: "#FFFFFF",
    status: true,
  },
  internship_about_7001: {
    id: "",
    title: "",
    title_color: "#000000",
    show_accent_rule: true,
    accent_color: "#9F1C33",
    content: "",
    image: "",
    image_mobile: "",
    image_alt: "",
    background_color: "#FFFFFF",
    status: true,
  },
  internship_registration_process_7002: {
    id: "",
    title: "Online Registration Process",
    title_color: "#031F44",
    show_accent_rule: true,
    accent_color: "#9F1C33",
    background_color: "#FEF2F4",
    show_help_bar: true,
    help_text: "Need help with internships?",
    help_email: "internship-programs@upgrad.com",
    help_phone: "",
    help_text_color: "#031F44",
    help_icon_color: "#2D1317",
    help_email_icon: "",
    help_phone_icon: "",
    help_bar_background_color: "#FFEEEE",
    help_bar_border_color: "#F6B2B2",
    metadata: [],
    status: true,
  },
  internship_courses_7003: {
    id: "",
    title: "All Internship Opportunities - Courses",
    title_color: "#031F44",
    show_accent_rule: true,
    accent_color: "#9F1C33",
    search_placeholder: "Search for Courses",
    search_bg_color: "",
    search_border_color: "#7C8493",
    search_text_color: "",
    search_placeholder_color: "#BEBEBE",
    search_icon: "",
    search_icon_color: "#9CA3AF",
    category_chip_active_bg_color: "#9F1C33",
    category_chip_active_text_color: "#FFFFFF",
    category_chip_bg_color: "#EAECF0",
    category_chip_text_color: "",
    apply_button_label: "Apply Now",
    load_more_label: "Load More",
    card_background_color: "#FFFFFF",
    card_border_color: "#F3F4F6",
    card_title_color: "#101828",
    card_badge_bg_color: "#FCD34D",
    card_badge_text_color: "#020617",
    card_detail_bg_color: "#FFF5F5",
    card_detail_text_color: "#020617",
    apply_button_bg_color: "#9F1C33",
    apply_button_hover_bg_color: "#8A1829",
    apply_button_text_color: "#FFFFFF",
    brochure_button_label: "Download Brochure",
    brochure_button_bg_color: "#FCE7F6",
    brochure_button_hover_bg_color: "#FBCFE8",
    brochure_button_text_color: "#9F1C33",
    brochure_button_border_color: "#FBCFE8",
    brochure_button_icon: "",
    load_more_bg_color: "#9F1C33",
    load_more_text_color: "#FFFFFF",
    status: true,
  },
  referral_form_1: {
    id: "",
    desktop_image: "",
    mobile_image: "",
    alt_image: "Referral form background",
    gradient_start_color: "rgba(0, 0, 0, 0.3)",
    gradient_end_color: "rgba(0, 0, 0, 0.7)",
    banner_title: "",
    form_title_text: "",
    form_title_color: "",
    form_description_text: "",
    form_description_color: "",
    button_text: "",
    button_text_color: "",
    button_color: "",
    verify_text: "",
    verify_text_color: "",
  },
  referral_form_1008: {
    id: "",
    logo_image: "",
    logo_alt: "Logo",
    desktop_image: "",
    mobile_image: "",
    thank_you_mobile_image: "",
    alt_image: "Referral form background",
    banner_title: "",
    form_header_background_color: "#E1473F",
    form_title_text: "Send Us a Message",
    form_title_color: "#FFFFFF",
    button_text: "Submit",
    button_text_color: "#FFFFFF",
    button_color: "#E1473F",
    resend_text_color: "#E1473F",
  },
  referral_form_1048: {
    id: "",
    logo_image: "",
    logo_alt: "Logo",
    desktop_image: "",
    mobile_image: "",
    side_image: "",
    alt_image: "Referral form background",
    banner_title: "",
    gradient_angle: "to right",
    gradient_start_color: "#F20113",
    gradient_end_color: "#A20113",
    form_title_text: "Send Us a Message",
    form_title_color: "#FFFFFF",
    button_text: "Submit",
    button_text_color: "#FFFFFF",
    button_color: "#FF0013",
    button_color_2: "#990013",
  },
  referral_form_1071: {
    id: "",
    logo_image: "",
    logo_alt: "Logo",
    desktop_image: "",
    mobile_image: "",
    side_image: "",
    alt_image: "Referral form background",
    banner_title: "",
    form_header_background_color: "#9F1C33",
    form_title_text: "Send Us a Message",
    form_title_color: "#FFFFFF",
    button_text: "Submit",
    button_text_color: "#FFFFFF",
    button_color: "#9F1C33",
    resend_text_color: "#9F1C33",
  },
  influencer_form_1: {
    id: "",
    desktop_image: "",
    mobile_image: "",
    alt_image: "Influencer form background",
    gradient_start_color: "rgba(0, 0, 0, 0.3)",
    gradient_end_color: "rgba(0, 0, 0, 0.7)",
    banner_title: "",
    form_title_text: "",
    form_title_color: "",
    form_description_text: "",
    form_description_color: "",
    button_text: "",
    button_text_color: "",
    button_color: "",
    verify_text: "",
    verify_text_color: "",
  },
  influencer_form_1007: {
    id: "",
    logo_image: "",
    logo_alt: "Logo",
    desktop_image: "",
    mobile_image: "",
    thank_you_mobile_image: "",
    alt_image: "Influencer form background",
    banner_title: "",
    form_header_background_color: "#E1473F",
    form_title_text: "Send Us a Message",
    form_title_color: "#FFFFFF",
    button_text: "Submit",
    button_text_color: "#FFFFFF",
    button_color: "#E1473F",
    resend_text_color: "#E1473F",
  },
  influencer_form_1049: {
    id: "",
    logo_image: "",
    logo_alt: "Logo",
    desktop_image: "",
    mobile_image: "",
    side_image: "",
    alt_image: "Influencer form background",
    banner_title: "",
    gradient_angle: "to right",
    gradient_start_color: "#F20113",
    gradient_end_color: "#A20113",
    form_title_text: "Send Us a Message",
    form_title_color: "#FFFFFF",
    button_text: "Submit",
    button_text_color: "#FFFFFF",
    button_color: "#FF0013",
    button_color_2: "#990013",
  },
  influencer_form_1072: {
    id: "",
    logo_image: "",
    logo_alt: "Logo",
    desktop_image: "",
    mobile_image: "",
    side_image: "",
    alt_image: "Influencer form background",
    banner_title: "",
    form_header_background_color: "#9F1C33",
    form_title_text: "Send Us a Message",
    form_title_color: "#FFFFFF",
    button_text: "Submit",
    button_text_color: "#FFFFFF",
    button_color: "#9F1C33",
    resend_text_color: "#9F1C33",
  },
  grievance_form_3000: {
    id: "",
    status: true,
    recipient_email: "",
    resend_api_key: "",
    from_email: "Grievance <onboarding@resend.dev>",
    email_subject: "Grievance: {subject} - {queryType}",
    heading: "Submit Your Grievance",
    heading_color: "#47456E",
    name_label: "Name",
    email_label: "Email Id",
    mobile_label: "Mobile No",
    query_type_label: "Query Type",
    query_type_options:
      "Academic,Administrative,Examination,Fee Related,Technical Issue,Others",
    category_label: "Category",
    category_options: "Complaint,Query,Feedback,Suggestion",
    subject_label: "Subject",
    description_label: "Description",
    attachment_enabled: true,
    submit_text: "SUBMIT",
    section_background_color: "#FFFFFF",
    card_background_color: "#F8F8F8",
    submit_button_color: "#CD5C5C",
    submit_button_text_color: "#FFFFFF",
  },
  mobile_lead_form_1: {
    id: "",
    status: true,
  },
  mobile_lead_form_2: {
    id: "",
    title: "Admission Closed on 02 Feb",
    title_color: "#FFFFFF",
    gradient_start_color: "#02A7B6",
    gradient_end_color: "#342B7C",
    gradient_angle: "180deg",
    enable_timer: true,
    timer_end_date: "",
    timer_end_time: "23:59:59",
    timer_number_color: "#222222",
    timer_text_color: "#222222",
    timer_background_color: "rgba(255, 255, 255, 0.8)",
    cta_text: "Apply Now",
    cta_button_text_color: "#222222",
    cta_button_color: "#FFFFFF",
    status: false,
  },
  mobile_lead_form_6: {
    id: "",
    status: true,
  },
  counseling_1: {
    id: "",
    title: "",
    title_text_color: "#000000",
    sub_title: "",
    sub_title_text_color: "#000000",
    cta_text: "Get Started",
    cta_text_color: "#FFFFFF",
    cta_button_background_color: "#000000",
    cta_button_icon: "",
    cta_button_icon_alt: "Get Started",
    background_color: "#FFFFFF",
    status: true,
  },
  program_prospects_1: {
    id: "",
    title: "",
    title_text_color: "#000000",
    sub_title: "",
    sub_title_text_color: "#000000",
    background_color: "#FFFFFF",
    status: false,
    metadata: [],
  },
  course_specialisations_1: {
    id: "",
    title: "Course Specialisations",
    title_text_color: "#000000",
    desktop_background_color: "#FFFFFF",
    mobile_variant_1_background_color: "#FFFFFF",
    mobile_variant_2_background_color: "#FFFFFF",
    mobile_variant: "1" as const,
    mobile_variant_1_left_nav_icon: "",
    mobile_variant_1_left_nav_icon_alt: "",
    mobile_variant_1_right_nav_icon: "",
    mobile_variant_1_right_nav_icon_alt: "",
    mobile_variant_2_top_arrow_icon: "",
    mobile_variant_2_top_arrow_icon_alt: "",
    mobile_variant_2_bottom_arrow_icon: "",
    mobile_variant_2_bottom_arrow_icon_alt: "",
    status: false,
    metadata: [],
  },
  accordion_1: {
    id: "",
    background_color: "#FFFFFF",
    status: false,
    metadata: [],
  },
  live_lecture_1: {
    id: "",
    status: false,
    title: "Live Lectures",
    title_text_color: "#1F2937",
    component_background_color: "#F9FAFB",
    day_section_color: "#FFFFFF",
    day_text_color: "#1F2937",
    view_button_text_color: "#374151",
    view_button_bg_color: "#FFFFFF",
    view_button_active_bg_color: "#3B82F6",
    view_button_active_text_color: "#FFFFFF",
    active_bar_color: "#3B82F6",
    navigate_button_color: "#374151",
    left_navigate_icon: "",
    right_navigate_icon: "",
    button_icon: "",
    button_text: "View All",
    button_text_color: "#FFFFFF",
    button_bg_color: "#3B82F6",
    event_color: "#10B981",
    event_text_color: "#1F2937",
    tooltip_color: "#1F2937",
    tooltip_text_color: "#FFFFFF",
    description: "",
  },
  live_lecture_1011: {
    id: "",
    status: false,
    title: "",
    title_text_color: "#171719",
    title_highlight_color: "#E1473F",
    component_background_color: "#FFFFFF",
    day_section_color: "#FFFFFF",
    day_text_color: "#171719",
    view_button_text_color: "#171719",
    view_button_bg_color: "#F3F4F6",
    view_button_active_bg_color: "#B8B8B8",
    view_button_active_text_color: "#171719",
    active_bar_color: "#B8B8B8",
    navigate_button_color: "#171719",
    left_navigate_icon: "",
    right_navigate_icon: "",
    button_icon: "",
    button_text: "Submit",
    button_text_color: "#FFFFFF",
    button_bg_color: "#E1473F",
    event_color: "#848484",
    event_text_color: "#FFFFFF",
    tooltip_color: "#991B1B",
    tooltip_text_color: "#FFFFFF",
    description: "* The above schedule are tentative in nature.",
  },
  live_lecture_1050: {
    id: "",
    status: false,
    title: "",
    title_text_color: "#171719",
    title_highlight_color: "#A10113",
    component_background_color: "#FFFFFF",
    day_section_color: "#FFFFFF",
    day_text_color: "#374151",
    view_button_text_color: "#171719",
    view_button_bg_color: "#F3F4F6",
    view_button_active_bg_color: "#B8B8B8",
    view_button_active_text_color: "#171719",
    navigate_button_color: "#171719",
    left_navigate_icon: "",
    right_navigate_icon: "",
    button_icon: "",
    button_text: "Submit",
    button_text_color: "#FFFFFF",
    button_bg_color: "",
    event_color: "#1F2937",
    event_text_color: "#FFFFFF",
    tooltip_color: "#991B1B",
    tooltip_text_color: "#FFFFFF",
    description: "* The above schedule are tentative in nature.",
  },
  program_benefits_1: {
    id: "",
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    font_style: "var(--font-merriweather)",
    pre_title: "",
    pre_title_color: "#222222CC",
    title: "",
    title_start_color: "#FFFFFF",
    title_end_color: "#00D4EB",
    title_gradient_direction: "90deg",
    button_text: "Apply Now",
    button_color: "#00D4EB",
    button_text_color: "#FFFFFF",
    load_more_button: "Load More",
    load_more_button_text_color: "#FFFFFF",
    status: false,
    enable_animation: false,
    metadata: [],
  },
  program_benefits_2: {
    id: "",
    desktop_background_color: "#000000",
    mobile_background_color: "#000000",
    backvariant: "variant_1",
    gradient_color_1: "#00D4EB",
    gradient_color_2: "",
    gradient_color_3: "",
    gradient_color_4: "",
    pre_title: "",
    pre_title_color: "#222222CC",
    title: "",
    title_start_color: "#FFFFFF",
    title_end_color: "#00D4EB",
    title_gradient_direction: "90deg",
    description: "",
    description_color: "#FFFFFFAF",
    scroll_frequency: 0,
    left_navigation_icon: "",
    alt_text_left_nav_icon: "",
    right_navigation_icon: "",
    alt_text_right_nav_icon: "",
    enable_animation: false,
    enable_stack_carousel: false,
    metadata: [],
  },
  program_benefits_12: {
    id: "",
    pre_title: "Why Choose",
    pre_title_color: "#E1473F",
    title: "IIT Kharagpur",
    title_start_color: "#171719",
    subtitle:
      "Your education should fit your life, not the other way around.",
    // Omit section_background_image so admin preview can fall back to persisted API URL
    section_overlay_rgba: "rgba(236,236,236,0.69)",
    status: false,
    metadata: [],
  },
  program_benefits_3: {
    id: "",
    title: "The Curriculum for Engineers Who Own the AI Layer.",
    title_start_color: "#171719",
    pre_title: "Design Systems, Build with AI, Ship to Production",
    pre_title_color: "#E15518",
    subtitle: "",
    section_overlay_rgba: "rgba(255,255,255,0.92)",
    card_hover_background_color: "#E15518",
    status: false,
    metadata: [],
  },
  program_benefits_4: {
    id: "",
    desktop_background_color: "#FFFFFF",
    title: "EPGC in AI",
    title_start_color: "#171719",
    pre_title: "– Native Software Engineering Course Fee",
    pre_title_color: "#E67E22",
    subtitle:
      "Flexible payment options and zero cost EMIs make your programme even more accessible.",
    button_text: "Apply Now",
    button_color: "#E67E22",
    button_text_color: "#FFFFFF",
    button_is_cta: true,
    status: false,
    metadata: [],
  },
  program_benefits_6: {
    id: "",
    desktop_background_color: "#FFFFFF",
    title: "Fee Structure {& Scholarships}",
    title_start_color: "#171719",
    title_end_color: "#E15518",
    subtitle:
      "Flexible payment options and zero cost EMIs make your programme even more accessible.",
    button_text: "Apply Now",
    button_color: "#EF4444",
    button_text_color: "#FFFFFF",
    button_is_cta: true,
    card_hover_background_color: "#EF4444",
    gradient_color_1: "#1D1D1F",
    gradient_color_2: "#B5460A",
    pre_title: "Prestigious Industry Certifications",
    description: "",
    status: false,
    metadata: [],
    metadata_1: [],
  },
  program_benefits_7: {
    id: "",
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    title: "",
    title_start_color: "#222222",
    title_end_color: "#E15518",
    description: "",
    image: "",
    mobile_image: "",
    image_alt: "",
    status: false,
    metadata: [],
  },
  program_benefits_1005: {
    id: "",
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    title: "Experience the {CU Online} Advantage",
    title_start_color: "#1F1F1F",
    title_end_color: "#E1473F",
    description: "",
    description_color: "#1F1F1F",
    button_text: "",
    button_color: "#E1473F",
    button_text_color: "#FFFFFF",
    card_hover_background_color: "#E1473F",
    status: false,
    metadata: [],
  },
  case_study_1: {
    id: "",
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#FFFFFF",
    backvariant: "variant_1",
    gradient_color_1: "",
    gradient_color_2: "",
    gradient_color_3: "",
    gradient_color_4: "",
    font_style: "var(--font-merriweather)",
    title: "Solve actual wealth",
    title_color: "#222222",
    title_highlight: "management challenges",
    title_highlight_color: "#342B7C",
    description: "",
    description_color: "rgba(0,0,0,0.7)",
    button_text: "Apply Now",
    button_start_color: "#342B7C",
    button_end_color: "#02A7B6",
    button_gradient_direction: "0deg",
    button_text_color: "#FFFFFF",
    left_navigation_icon: "",
    alt_text_left_nav_icon: "",
    right_navigation_icon: "",
    alt_text_right_nav_icon: "",
    scroll_frequency: 0,
    enable_animation: false,
    status: false,
    metadata: [],
  },
  brand_1: {
    id: "",
    desktop_background_color: "#000000",
    mobile_background_color: "#000000",
    backvariant: "variant_3",
    gradient_color_1: "",
    gradient_color_2: "",
    gradient_color_3: "",
    gradient_color_4: "",
    font_style: "var(--font-google-sans)",
    pre_title: "Solve real business challenges",
    pre_title_color: "#FFFFFF",
    title: "for leading brands",
    title_color: "#02A7B6",
    description: "",
    description_color: "rgba(255,255,255,0.7)",
    enable_animation: false,
    status: false,
    metadata: [],
  },
  institutes_1: {
    id: "",
    desktop_background_color: "#000000",
    mobile_background_color: "#000000",
    backvariant: "variant_3",
    gradient_color_1: "",
    gradient_color_2: "",
    gradient_color_3: "",
    gradient_color_4: "",
    font_style: "var(--font-merriweather)",
    pre_title: "Graduates build careers across",
    pre_title_color: "#FFFFFF",
    title: "leading financial institutions",
    title_color: "#02A7B6",
    description:
      "With the CFP® Global Certification, graduates can pursue international advisory roles, work with global clients, and build cross-border careers in financial planning.",
    description_color: "rgba(255,255,255,0.7)",
    pill_background_color: "rgba(86,226,245,0.2)",
    pill_text_color: "#FFFFFF",
    pill_border_color: "rgba(255,255,255,0.2)",
    enable_animation: false,
    status: false,
    metadata: [],
  },
  course_outcome_1: {
    id: "",
    status: false,
    desktop_background_color: "",
    mobile_background_color: "",
    backvariant: "variant_1",
    font_family_variant: "variant_2",
    font_style: "var(--font-merriweather)",
    pre_title: "",
    pre_title_text_color: "#000000",
    title: "",
    title_gradient_angle: "90deg",
    title_gradient_start_color: "#FFFFFF",
    title_gradient_end_color: "#00D4EB",
    description: "",
    description_color: "#000000",
    load_more_button: "Load More",
    load_more_button_text_color: "#FFFFFF",
    read_more_text: "",
    read_more_color: "#000000",
    metadata: [],
  },

  /**
   * MOOC Form 3000. Defaults mirror the reference form's own copy, so a section
   * dropped on a page and never edited renders exactly like the original.
   */
  mooc_form_3000: {
    id: "",
    status: true,
    cta_show: true,
    cta_text: "Enroll Now",
    cta_background_color: "#9F1C33",
    cta_text_color: "#FFFFFF",
    cta_align: "center",
    section_background_color: "#FFFFFF",
    form_title: "Enroll for free",
    form_subtitle: "",
    title_color: "#031F44",
    subtitle_color: "#6B7280",
    label_color: "#020817",
    submit_button_color: "#9F1C33",
    submit_button_text_color: "#FFFFFF",
    accent_color: "#031F44",
    error_color: "#EF4444",
    name_label: "Student Name (as per 10th mark sheet)",
    name_placeholder: "Enter your name",
    email_label: "Email",
    email_placeholder: "Enter your email",
    mobile_label: "Mobile Number",
    send_otp_text: "Send OTP",
    sending_otp_text: "Sending...",
    otp_label: "OTP sent to +91 {mobile}",
    otp_placeholder: "Enter OTP",
    verify_text: "Verify",
    verifying_text: "Verifying...",
    edit_details_text: "← Edit email or mobile",
    resend_text: "Resend OTP",
    resend_countdown_text: "Resend OTP in 00:{seconds}",
    dob_label: "Date of Birth",
    city_label: "City",
    city_placeholder: "Enter your city",
    state_label: "State",
    state_placeholder: "Enter your state",
    abc_id_label: "ABC ID (optional)",
    abc_id_placeholder: "Enter your 12-digit ABC ID",
    qualification_label: "Highest Qualification",
    qualification_placeholder: "Select qualification",
    qualification_options:
      "12th Std\nPursuing UG\nPursuing PG\nGraduate\nPost Graduate",
    courses_label: "Select KUK-e-Learn Asynchronous Course",
    courses_help_text: "You can select one or more courses.",
    course_options: [
      "Communication in Professional Life",
      "Office and Spreadsheet Tools",
      "Business Management",
      "Soft Skills",
      "Organisational Behaviour",
      "Marketing Management",
      "Financial Literacy",
      "Entrepreneurship Development",
      "Business Environment",
      "Human Resource Management",
      "Banking and Insurance",
    ].join("\n"),
    submit_text: "Submit",
    submitting_text: "Submitting...",
    success_title: "Thank you!",
    success_note_prefix: "NOTE:",
    success_message:
      "You will receive your LMS login credentials after the forthcoming Monday on your registered Email ID.",
    close_text: "Close",
    name_error: "Please enter your name as per your 10th mark sheet",
    email_error: "Please enter a valid email address",
    mobile_error: "Please enter a valid 10-digit mobile number",
    otp_error: "Please enter the OTP",
    dob_error: "Please enter your date of birth",
    dob_future_error: "Date of birth cannot be in the future",
    city_error: "Please enter your city",
    state_error: "Please enter your state",
    abc_id_error: "ABC ID must be exactly 12 digits",
    qualification_error: "Please select your highest qualification",
    courses_error: "Please select at least one course",
    submit_error: "Could not submit your details. Please try again.",
  },

  // ── KUK auxiliary pages (3001) ────────────────────────────────────────────
  banner_3001: {
    id: "",
    status: true,
    image: "",
    mobile_image: "",
    image_alt: "",
    bleed_background_color: "#FFFFFF",
    title: "",
    title_color: "#031F44",
    gradient_color_1: "rgba(255, 255, 255, 0.92)",
    gradient_color_2: "rgba(255, 255, 255, 0.00)",
    icon_color: "#031F44",
    hero_section_height: 300,
    hero_container_max_width: 1120,
    metadata: [],
  },

  banner_5004: {
    id: "",
    status: true,
    backvariant: "variant_1",
    image: "",
    mobile_image: "",
    image_alt: "",
    background_color: "transparent",
    bleed_background_color: "",
    horizontal_stripe_color: "rgba(255, 255, 255, 0.80)",
    card_title_color: "#101112",
    metadata_1: [],
    hero_section_height: 366,
    title: "",
    title_color: "#FFFFFF",
    description: "",
    description_color: "#FFFFFF",
    apply_now_show: true,
    apply_now_text: "Get in touch",
    apply_now_is_cta: true,
    apply_now_link: "",
    apply_now_color: "transparent",
    apply_now_border_color: "#FFFFFF",
    cta_text_color: "#FFFFFF",
    gradient_color_3: "",
    gradient_color_4: "",
    // No repeating rows — but `banner.metadata` is NOT NULL, so the row still
    // carries the empty array every other banner variant stores.
    metadata: [],
  },

  // ── KUK landing pages — section widgets (5006-5016) ───────────────────────
  key_numbers_5006: { id: "", status: true, metadata: [] },
  key_numbers_5007: { id: "", status: true, metadata: [] },
  program_benefits_5008: { id: "", status: true, metadata: [], metadata_1: [] },
  program_benefits_5009: { id: "", status: true, metadata: [], metadata_1: [] },
  recruiters_5010: { id: "", status: true, metadata: [] },
  pre_login_referral_5019: { id: "", status: true },
  post_login_referral_5020: { id: "", status: true, metadata: [] },
  refer_friend_5021: { id: "", status: true },
  launchpad_video_5023: { id: "", status: true },
  launchpad_video_5024: { id: "", status: true },
  dashboard_access_5025: { id: "", status: true },
  referral_dashboard_5022: { id: "", status: true },
  application_process_5011: { id: "", status: true, metadata_1: [], metadata_2: [] },
  eligibility_5012: { id: "", status: true },
  faq_5013: { id: "", status: true },
  footer_5014: { id: "", status: true },
  header_5015: { id: "", status: true },
  header_5016: { id: "", status: true },

  // ── KUK home page (kukonline.ac.in) — variants 1051-1060 ──────────────────
  banner_1051: {
    id: "",
    status: true,
    metadata: [],
    slide_frequency: 5000,
    hero_section_height: 0,
    title_1: "Welcome to",
    title_1_color: "#031F44",
    title: "",
    title_color: "#031F44",
    title_2: "Online",
    title_2_color: "#9F1C33",
    description: "",
    description_color: "#031F44",
    background_color: "rgba(255, 255, 255, 0.9)",
    carousel_color: "rgba(0, 0, 0, 0.5)",
    apply_now_show: true,
    apply_now_text: "Enroll Now",
    apply_now_is_cta: true,
    apply_now_link: "",
    button_color: "#9F1C33",
    apply_now_color: "#FFFFFF",
  },
  accreditation_1052: {
    id: "",
    status: true,
    metadata: [],
    background_color: "rgba(159, 28, 51, 0.2)",
    title_text_color: "#031F44",
    divider_color: "#9F1C3345",
  },
  program_cards_1053: {
    id: "",
    status: true,
    metadata: [],
    title: "Featured Programmes",
    title_text_color: "#031F44",
    gradient_color_1: "#9F1C33",
    desktop_background_color: "#FFFFFF",
    mobile_background_color: "#F0F2F5",
    card_hover_background_color: "#9F1C33",
    card_hover_text_color: "#FFFFFF",
    title_start_color: "#031F44",
    card_background_color: "#FFFFFF",
    gradient_color_2: "#9F1C33",
    title_end_color: "#031F44",
    gradient_color_3: "#FFFFFF",
    salary_text_color: "#344054",
    key_skills_background_color: "#FFD964",
    key_skills_text_color: "#031F44",
    gradient_color_4: "#FEE2E2",
    pre_title_color: "#9F1C33",
    key_skills_border_color: "#9F1C3333",
    pre_title: "Specialisations",
    description: "Specializations",
    note: "Duration:",
    cta_text: "Know more",
    cta_text_color: "#031F44",
    load_more_text: "Brochure",
    load_more_mobile_background_color: "#9F1C33",
    load_more_desktop_text_color: "#FFFFFF",
    load_more_desktop_icon: "download",
  },
  about_1054: {
    id: "",
    status: true,
    // No repeating rows — but `about.metadata` is NOT NULL, so the row still
    // carries the empty array every other About variant stores.
    metadata: [],
    title: "About Us",
    title_text_color: "#031F44",
    highlight_border_color: "#9F1C33",
    description: "",
    description_text_color: "#031F44",
    video_link: "",
    desktop_image: "",
    alt_image: "",
    background_color: "#F8F8F8",
    read_more_text: "Know More",
    read_more_color: "#031F44",
    is_cta: false,
    cta_link: "/about",
  },
  key_numbers_1055: {
    id: "",
    status: true,
    metadata: [],
    gradient_color_1: "#000000",
    gradient_color_2: "#1E40AF",
    gradient_color_3: "#93C5FD",
    title_start_color: "#FFFFFF",
    description_color: "#FFFFFF",
    description: "+",
  },
  testimonials_1056: {
    id: "",
    status: true,
    metadata: [],
    title: "Student Testimonials",
    title_start_color: "#031F44",
    gradient_color_1: "#9F1C33",
    background_color: "#FFFFFF",
    card_color: "rgba(159, 28, 51, 0.1)",
    mobile_background_color: "#FFFFFF",
    title_end_color: "#031F44",
    gradient_color_2: "#344054",
    gradient_color_3: "#031F44",
    gradient_color_4: "#475467",
    button_color: "#FFFFFF",
    button_text_color: "#031F44",
    left_navigation_icon: "",
    left_navigation_icon_alt: "Previous",
    right_navigation_icon: "",
    right_navigation_icon_alt: "Next",
    pre_title_color: "#031F44",
    desktop_background_color: "#6B7280",
  },
  about_1057: {
    id: "",
    status: true,
    metadata: [],
    title: "From Vice Chancellor's Desk",
    title_text_color: "#031F44",
    highlight_border_color: "#9F1C33",
    desktop_image: "",
    alt_image: "",
    title_1: "",
    title_1_color: "#031F44",
    sub_text: "",
    sub_text_color: "#64748B",
    description: "",
    description_text_color: "#031F44",
    read_more_text: "Know More",
    read_more_color: "#9F1C33",
    is_cta: false,
    cta_link: "/vice-chancellor",
    gradient_color_2: "#FFFFFF",
    key_highlight_divider_color: "#E2E8F0",
    gradient_color_1: "rgba(159, 28, 51, 0.1)",
    key_highlight_heading_color: "#031F44",
    key_highlight_subtext_color: "#64748B",
    background_color: "#FFFFFF",
  },
  banner_1058: {
    id: "",
    status: true,
    // No repeating rows — but `banner.metadata` is NOT NULL, so the row still
    // carries the empty array every other banner variant stores.
    metadata: [],
    background_image: "",
    background_image_mobile: "",
    bleed_background_color: "rgba(0, 0, 0, 0.5)",
    hero_section_height: 285,
    icon: "",
    alt_icon: "",
    icon_color: "#FFFFFF",
    title: "",
    title_color: "#FFFFFF",
    apply_now_show: true,
    apply_now_text: "Get in touch",
    apply_now_is_cta: true,
    apply_now_link: "",
    apply_now_color: "#FFFFFF",
    apply_now_border_color: "#FFFFFF",
  },
  banner_1059: {
    id: "",
    status: true,
    metadata: [],
    background_image: "",
    background_image_mobile: "",
    image_alt: "",
    title: "",
    title_color: "#FFFFFF",
    title_highlight_color: "#E26C24",
    description: "",
    description_color: "#FFFFFF",
    icon: "",
    alt_icon: "",
    apply_now_show: true,
    apply_now_text: "Start referring Now!",
    apply_now_is_cta: true,
    apply_now_link: "",
    apply_now_color: "#FFFFFF",
    gradient_color_1: "#B0325E",
    gradient_color_2: "#FAA681",
    gradient_color_3: "47deg",
  },
  menu_item_1060: {
    id: "",
    parent_id: null,
    title: "",
    slug: "",
    url: "",
    status: true,
    priority: 1,
    text_color: "",
    icon: "",
    icon_alt: "",
    is_button: false,
    button_color: "",
  },

  // ── KUK programme pages — section widgets (1061-1070) ─────────────────────
  banner_1061: {
    id: "",
    status: true,
    metadata: [],
    metadata_1: [],
    slide_frequency: 5000,
    hero_section_height: 0,
    background_color: "rgba(255, 255, 255, 0.3)",
    title_color: "#031F44",
    divider_color: "#FFFFFF",
    carousel_color: "rgba(0, 0, 0, 0.5)",
    apply_now_show: true,
    apply_now_text: "Apply",
    apply_now_is_cta: true,
    apply_now_link: "",
    button_color: "#9F1C33",
    apply_now_color: "#FFFFFF",
    download_brochure_show: true,
    download_brochure_text: "Brochure",
    download_brochure_is_cta: true,
    download_brochure_link: "",
    apply_now_border_color: "rgba(255, 255, 255, 0.34)",
    icon: "download",
  },
  banner_1062: {
    id: "",
    status: true,
    metadata: [],
    background_image: "",
    background_image_mobile: "",
    image_alt: "",
    title: "",
    title_color: "#FFFFFF",
    title_highlight_color: "#E26C24",
    description: "",
    description_color: "#FFFFFF",
    icon: "",
    apply_now_show: true,
    apply_now_text: "Start referring Now!",
    apply_now_is_cta: true,
    apply_now_link: "",
    apply_now_color: "#FFFFFF",
    gradient_color_1: "#B0325E",
    gradient_color_2: "#FAA681",
    gradient_color_3: "47deg",
  },
  program_benefits_1063: {
    id: "",
    status: true,
    metadata: [],
    metadata_1: [],
    title: "Why Choose Us?",
    title_start_color: "#101112",
    title_end_color: "#9F1C33",
    desktop_background_color: "#FFFFFF",
    card_hover_background_color: "#FFFFFF",
    gradient_color_1: "#9F1C33",
    subtitle_color: "#101112",
    description_color: "#4B4B4B",
    button_text: "Get in touch",
    button_icon_url: "chat",
    button_color: "#9F1C33",
    button_text_color: "#FFFFFF",
  },
  program_structure_1064: {
    id: "",
    status: true,
    gradient_color_1: "#9F1C33",
    metadata: [],
    title: "Programme Structure",
    title_text_color: "#101112",
    section_background_image: "",
    section_background_image_alt: "",
    section_background_color: "#F5F5F5",
    subject_container_color: "#FFFFFF",
    header_section_color: "#9F1C33",
    subjects_column_label: "Subject",
    electives_tab_label: "As per elective",
    card_background_color: "rgba(159, 28, 51, 0.2)",
    section_description: "",
    brochure_cta_text: "Brochure",
    brochure_cta_text_color: "#FFFFFF",
    cta_button_color: "#9F1C33",
    cta_button_icon: "download",
    brochure_cta_is_cta: true,
    cta_button_text: "",
  },
  elective_1065: {
    id: "",
    status: true,
    title: "Electives",
    title_text_color: "#101112",
    sub_title: "",
    sub_title_text_color: "#4B4B4B",
    background_color: "#FFFFFF",
  },
  program_fee_1066: {
    id: "",
    status: true,
    gradient_color_1: "#9F1C33",
    metadata: [],
    metadata_1: [],
    categories: [],
    title_1: "Fee Structure",
    title_1_color: "#101112",
    metadata_title: "Additional Fee:",
    metadata_title_color: "#101112",
    background_color: "#FFFFFF",
    table_gradient_from: "#FFFFFF",
    table_gradient_to: "#FFCF88",
    title_2_gradient_from: "#9F1C33",
    title_2_gradient_to: "#9C9C9C",
    cta_text: "Get Started",
    cta_text_color: "#101112",
    cta_gradient_from: "#F3F3F3",
  },
  program_cards_1067: {
    id: "",
    status: true,
    gradient_color_1: "#9F1C33",
    metadata: [],
    pre_title: "Didn't find what you need?",
    pre_title_color: "#101112",
    title: "Explore other Specialisations",
    title_text_color: "#101112",
    title_highlight_color: "#9F1C33",
    desktop_background_color: "#FFFFFF",
    card_background_color: "#FFFFFF",
    mobile_background_color: "#FEE2E2",
    salary_text_color: "#4B4B4B",
    note: "Duration:",
    cta_icon: "graduation",
    cta_text: "Know More",
    cta_text_color: "#47456E",
    load_more_text: "Brochure",
    load_more_mobile_background_color: "#9F1C33",
    load_more_desktop_text_color: "#FFFFFF",
    load_more_desktop_icon: "download",
    show_features: true,
  },
  banner_1068: {
    id: "",
    status: true,
    metadata: [],
    background_image: "",
    background_image_mobile: "",
    image_alt: "",
    icon: "gift",
    icon_color: "#FFFFFF",
    title: "Access your exclusive dashboard!",
    title_color: "#FFFFFF",
    alt_icon: "",
    apply_now_show: true,
    apply_now_text: "Refer & Earn",
    apply_now_is_cta: true,
    apply_now_link: "",
    apply_now_color: "#FFFFFF",
    apply_now_border_color: "#FFFFFF",
  },
  program_benefits_1069: {
    id: "",
    status: true,
    metadata: [],
    metadata_1: [],
    title: "Learn More",
    title_start_color: "#101112",
    title_end_color: "#9F1C33",
    desktop_background_color: "rgba(159, 28, 51, 0.1)",
    card_hover_background_color: "#FFFFFF",
    subtitle_color: "#101112",
    description_color: "#101112",
    load_more_button: "View more",
    load_more_button_text_color: "#9F1C33",
  },
  program_cards_1070: {
    id: "",
    status: true,
    gradient_color_1: "#9F1C33",
    metadata: [],
    pre_title: "Didn't find what you need?",
    pre_title_color: "#101112",
    title: "Explore other courses",
    title_text_color: "#101112",
    background_image: "",
    desktop_background_color: "#FFFFFF",
    card_background_color: "#FFFFFF",
    salary_text_color: "#4A5568",
    cta_text: "View",
    cta_text_color: "#9F1C33",
    cta_background_start_color: "#F7F7F7",
  },

  // ── BDU auxiliary pages (6000+) ───────────────────────────────────────────
  banner_6000: {
    id: "",
    status: true,
    image: "",
    mobile_image: "",
    image_alt: "",
    title: "",
    title_color: "#FFFFFF",
    hero_section_height: 418,
    background_color: "#22405ADB",
    gradient_color_1: "#13344F",
    gradient_color_2: "#77B6EB",
    carousel_color: "#FFFFFF",
    metadata: [],
  },
  rich_text_editor_6001: {
    id: "",
    content: "",
    background_color: "#FFFFFF",
    enable_lead_form: false,
    status: false,
  },
  rich_text_editor_6003: {
    id: "",
    content: "",
    background_color: "#FFFFFF",
    enable_lead_form: false,
    status: false,
  },
  rich_text_editor_3000: {
    id: "",
    content: "",
    status: false,
  },skill_cards_3000: {
    id: "",
    heading: "Equip Yourself For What's Next",
    heading_color: "#101112",
    sub_heading: "",
    sub_heading_color: "#101112",
    cta_label: "Apply Now",
    cta_action: "apply",
    cta_icon: "",
    cta_url: "",
    metadata: [],
    status: true,
  }
};

// Create the slice
const pageSlice = createSlice({
  name: "page",
  initialState,
  reducers: {
    setBanner1: (state, action: PayloadAction<IBanner>) => {
      state.banner_1 = action.payload;
    },
    setBanner2: (state, action: PayloadAction<IBanner>) => {
      state.banner_2 = action.payload;
    },
    setBanner3: (state, action: PayloadAction<IBanner | Record<string, unknown>>) => {
      state.banner_3 = normalizeBanner3Payload({
        ...(state.banner_3 as unknown as Record<string, unknown>),
        ...(action.payload as Record<string, unknown>),
      });
    },
    setBanner4: (state, action: PayloadAction<IBanner>) => {
      state.banner_4 = action.payload;
    },
    setBanner5: (state, action: PayloadAction<IBanner>) => {
      state.banner_5 = action.payload;
    },
    setBanner6: (state, action: PayloadAction<IBanner>) => {
      state.banner_6 = action.payload;
    },
    setBanner8: (state, action: PayloadAction<IBanner8>) => {
      state.banner_8 = action.payload;
    },
    setBanner15: (state, action: PayloadAction<IBanner15>) => {
      state.banner_15 = action.payload;
    },
    setBanner1010: (state, action: PayloadAction<IBanner1010>) => {
      state.banner_1010 = action.payload;
    },
    setBanner9: (state, action: PayloadAction<IBanner>) => {
      state.banner_9 = action.payload;
    },
    setBanner10: (state, action: PayloadAction<IBanner>) => {
      state.banner_10 = action.payload;
    },
    setBanner11: (state, action: PayloadAction<IBanner>) => {
      state.banner_11 = action.payload;
    },
    setBanner3000: (state, action: PayloadAction<IBanner>) => {
      state.banner_3000 = action.payload;
    },
    setBanner1025: (state, action: PayloadAction<IBanner>) => {
      state.banner_1025 = action.payload;
    },
    setKeyNumbers1026: (state, action: PayloadAction<IKeyNumbers>) => {
      state.key_numbers_1026 = action.payload;
    },
    setProgramCards1027: (state, action: PayloadAction<IProgramCards>) => {
      state.program_cards_1027 = action.payload;
    },
    setAbout1028: (state, action: PayloadAction<IAbout>) => {
      state.about_1028 = action.payload;
    },
    setProgramBenefits1029: (state, action: PayloadAction<IProgramBenefits>) => {
      state.program_benefits_1029 = action.payload;
    },
    setApplicationProcess1030: (state, action: PayloadAction<IApplicationProcess>) => {
      state.application_process_1030 = action.payload;
    },
    setAcademicDeliverables1031: (state, action: PayloadAction<IAcademicDeliverables>) => {
      state.academic_deliverables_1031 = action.payload;
    },
    setLeaders1032: (state, action: PayloadAction<ILeaders>) => {
      state.leaders_1032 = action.payload;
    },
    setFaq1033: (state, action: PayloadAction<IFaq>) => {
      state.faq_1033 = action.payload;
    },
    setFooter1034: (state, action: PayloadAction<IFooter>) => {
      state.footer_1034 = action.payload;
    },
    setBanner1035: (state, action: PayloadAction<IBanner>) => {
      state.banner_1035 = action.payload;
    },
    setKeyNumbers1036: (state, action: PayloadAction<IKeyNumbers>) => {
      state.key_numbers_1036 = action.payload;
    },
    setAbout1037: (state, action: PayloadAction<IAbout>) => {
      state.about_1037 = action.payload;
    },
    setAbout5005: (state, action: PayloadAction<IAbout>) => {
      state.about_5005 = action.payload;
    },
    setProgramCards1038: (state, action: PayloadAction<IProgramCards>) => {
      state.program_cards_1038 = action.payload;
    },
    setKeyNumbers1039: (state, action: PayloadAction<IKeyNumbers>) => {
      state.key_numbers_1039 = action.payload;
    },
    setProgramBenefits1040: (state, action: PayloadAction<IProgramBenefits>) => {
      state.program_benefits_1040 = action.payload;
    },
    setEligibility1041: (state, action: PayloadAction<IEligibility>) => {
      state.eligibility_1041 = action.payload;
    },
    setProgramStructure1042: (
      state,
      action: PayloadAction<IProgramStructure1042>,
    ) => {
      state.program_structure_1042 = action.payload;
    },
    setProgramFee1043: (state, action: PayloadAction<IProgramFee>) => {
      state.program_fee_1043 = action.payload;
    },
    setProgramBenefits1044: (state, action: PayloadAction<IProgramBenefits>) => {
      state.program_benefits_1044 = action.payload;
    },
    setProgramCards1045: (state, action: PayloadAction<IProgramCards>) => {
      state.program_cards_1045 = action.payload;
    },
    setProgramCards6002: (state, action: PayloadAction<IProgramCards>) => {
      state.program_cards_6002 = action.payload;
    },
    setProgramCards6004: (state, action: PayloadAction<IProgramCards>) => {
      state.program_cards_6004 = action.payload;
    },
    setHeader1: (state, action: PayloadAction<IHeader1>) => {
      state.header_1 = action.payload;
    },
    setHeader2: (state, action: PayloadAction<IHeader2>) => {
      state.header_2 = action.payload;
    },
    setHeader3: (state, action: PayloadAction<IHeader3>) => {
      state.header_3 = action.payload;
    },
    setHeader4: (state, action: PayloadAction<IHeader4>) => {
      state.header_4 = action.payload;
    },
    setHeader5: (state, action: PayloadAction<IHeader5>) => {
      state.header_5 = action.payload;
    },
    setHeader6: (state, action: PayloadAction<IHeader6>) => {
      state.header_6 = action.payload;
    },
    setHeader7: (state, action: PayloadAction<IHeader7>) => {
      state.header_7 = action.payload;
    },
    setHeader1046: (state, action: PayloadAction<IHeader1046>) => {
      state.header_1046 = action.payload;
    },
    setMenuItem: (state, action: PayloadAction<IMenuItem>) => {
      state.menu_item = action.payload;
    },
    setMenuItem2: (state, action: PayloadAction<IMenuItem2>) => {
      state.menu_item_2 = action.payload;
    },
    setMenuItem3: (state, action: PayloadAction<IMenuItem3>) => {
      state.menu_item_3 = action.payload;
    },
    setMenuItem4: (state, action: PayloadAction<IMenuItem4>) => {
      state.menu_item_4 = action.payload;
    },
    setMenuItem5: (state, action: PayloadAction<IMenuItem5>) => {
      state.menu_item_5 = action.payload;
    },
    setMenuItem2000: (state, action: PayloadAction<IMenuItem2000>) => {
      state.menu_item_2000 = action.payload;
    },
    setMenuItem1047: (state, action: PayloadAction<IMenuItem1047>) => {
      state.menu_item_1047 = action.payload;
    },
    setAbout1: (state, action: PayloadAction<IAbout>) => {
      state.about_1 = action.payload;
    },
    setAbout2: (state, action: PayloadAction<IAbout2>) => {
      state.about_2 = action.payload;
    },
    setAbout5: (state, action: PayloadAction<IAbout>) => {
      state.about_5 = action.payload;
    },
    setAbout4: (state, action: PayloadAction<IAbout>) => {
      state.about_4 = action.payload;
    },
    setAbout3: (state, action: PayloadAction<IAbout3>) => {
      state.about_3 = action.payload;
    },
    setKeyNumbers1: (state, action: PayloadAction<IKeyNumbers>) => {
      state.key_numbers_1 = action.payload;
    },
    setKeyNumbers2: (state, action: PayloadAction<IKeyNumbers2>) => {
      state.key_numbers_2 = action.payload;
    },
    setDigitalLearning1: (state, action: PayloadAction<IDigitalLearning>) => {
      const p = action.payload;
      state.digital_learning_1 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setAccreditation1: (state, action: PayloadAction<IAccreditation>) => {
      state.accreditation_1 = action.payload;
    },
    setAccreditation2: (state, action: PayloadAction<IAccreditation>) => {
      state.accreditation_2 = action.payload;
    },
    setAccreditation3: (state, action: PayloadAction<IAccreditation>) => {
      state.accreditation_3 = action.payload;
    },
    setAccreditation4: (state, action: PayloadAction<IAccreditation>) => {
      state.accreditation_4 = action.payload;
    },
    setAccreditation5: (state, action: PayloadAction<IAccreditation>) => {
      state.accreditation_5 = action.payload;
    },
    setAccreditation1000: (state, action: PayloadAction<IAccreditation>) => {
      state.accreditation_1000 = action.payload;
    },
    setAcademicDeliverables1: (
      state,
      action: PayloadAction<IAcademicDeliverables>
    ) => {
      state.academic_deliverables_1 = action.payload;
    },
    setAcademicDeliverables2: (
      state,
      action: PayloadAction<IAcademicDeliverables2>
    ) => {
      state.academic_deliverables_2 = action.payload;
    },
    setAcademicDeliverables1001: (
      state,
      action: PayloadAction<IAcademicDeliverables1001>
    ) => {
      state.academic_deliverables_1001 = action.payload;
    },
    setAcademicDeliverables1002: (
      state,
      action: PayloadAction<IAcademicDeliverables1002>
    ) => {
      state.academic_deliverables_1002 = action.payload;
    },
    setAcademicDeliverables1006: (
      state,
      action: PayloadAction<IAcademicDeliverables1006>
    ) => {
      state.academic_deliverables_1006 = action.payload;
    },
    setScholar1: (state, action: PayloadAction<IScholar>) => {
      state.scholar_1 = action.payload;
    },
    setFaq1: (state, action: PayloadAction<IFaq>) => {
      state.faq_1 = action.payload;
    },
    setFaq2: (state, action: PayloadAction<IFaq2>) => {
      const p = action.payload;
      state.faq_2 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setVisionaries1: (state, action: PayloadAction<IVisionaries>) => {
       const p = action.payload;
      state.visionaries_1 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
        enable_coverflow_carousel: !(
          p.enable_coverflow_carousel === false ||
          p.enable_coverflow_carousel === 0
        ),
      };
    },
    setLearnFromLeaders: (state, action: PayloadAction<ILearnFromLeaders>) => {
      const p = action.payload;
      state.learn_from_leaders = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setLearnFromLeaders1003: (
      state,
      action: PayloadAction<ILearnFromLeaders>
    ) => {
      state.learn_from_leaders_1003 = action.payload;
    },
    setFaq3: (state, action: PayloadAction<IFaq3>) => {
      state.faq_3 = action.payload;
    },
    setFaq4: (state, action: PayloadAction<IFaq4>) => {
      state.faq_4 = action.payload;
    },
    setProgramStructure1: (state, action: PayloadAction<IProgramStructure>) => {
      state.program_structure_1 = action.payload;
    },
    setElective1: (state, action: PayloadAction<IElective>) => {
      state.elective_1 = action.payload;
    },
    setProgramFee1: (state, action: PayloadAction<IProgramFee>) => {
      state.program_fee_1 = action.payload;
    },
    setProgramFee2: (state, action: PayloadAction<IProgramFeeV2>) => {
      state.program_fee_2 = action.payload;
    },
    setProgramStructure2: (state, action: PayloadAction<IProgramStructureV2>) => {
      state.program_structure_2 = action.payload;
    },
    setProgramStructure3: (state, action: PayloadAction<IProgramStructure3>) => {
      state.program_structure_3 = action.payload;
    },
    setProgramStructure4: (state, action: PayloadAction<IProgramStructure4>) => {
      state.program_structure_4 = action.payload;
    },
    setProgramStructure5: (state, action: PayloadAction<IProgramStructure5>) => {
      state.program_structure_5 = action.payload;
    },
    setRecruiters1: (state, action: PayloadAction<IRecruiters>) => {
      state.recruiters_1 = action.payload;
    },
    setEligibility1: (state, action: PayloadAction<IEligibility>) => {
      state.eligibility_1 = action.payload;
    },
    setEligibility2: (state, action: PayloadAction<IEligibility2>) => {
      state.eligibility_2 = action.payload;
    },
    setSpecialisation1: (state, action: PayloadAction<ISpecialisation>) => {
      state.specialisation_1 = action.payload;
    },
    setSpecialisation5017: (state, action: PayloadAction<ISpecialisation>) => {
      state.specialisation_5017 = action.payload as PageState["specialisation_5017"];
    },
    setCareerProspects1: (state, action: PayloadAction<ICareerProspects>) => {
      state.career_prospects_1 = action.payload;
    },
    setCareerProspects2: (state, action: PayloadAction<ICareerProspects2>) => {
      state.career_prospects_2 = action.payload;
    },
    setCareerProspects3: (state, action: PayloadAction<ICareerProspects3>) => {
      state.career_prospects_3 = action.payload;
    },
    setCareerProspects4: (state, action: PayloadAction<ICareerProspects4>) => {
      state.career_prospects_4 = action.payload;
    },
    setCertification1: (state, action: PayloadAction<ICertification>) => {
      state.certification_1 = action.payload;
    },
    setApplicationProcess1: (state, action: PayloadAction<IApplicationProcess>) => {
      state.application_process_1 = action.payload;
    },
    setApplicationProcess2: (state, action: PayloadAction<IApplicationProcess>) => {
      state.application_process_2 = action.payload;
    },
    setSkills1: (state, action: PayloadAction<ISkills>) => {
      state.skills_1 = action.payload;
    },
    setComparison1: (state, action: PayloadAction<IComparison>) => {
      state.comparison_1 = action.payload;
    },
    setComparison1004: (state, action: PayloadAction<IComparison>) => {
      state.comparison_1004 = action.payload;
    },
    setLeaders1: (state, action: PayloadAction<ILeaders>) => {
      state.leaders_1 = action.payload;
    },
    setWhoIsThisProgramFor1: (state, action: PayloadAction<IWhoIsThisProgramFor>) => {
      state.who_is_this_program_for_1 = action.payload;
    },
    setBannerVideo1: (state, action: PayloadAction<IBannerVideo>) => {
      state.banner_video_1 = action.payload;
    },
    setWalkThrough1: (state, action: PayloadAction<IWalkThrough>) => {
      state.walk_through_1 = action.payload;
    },
    setLifeMilestone1: (state, action: PayloadAction<ILifeMilestone>) => {
      state.life_milestone_1 = action.payload;
    },
    setLifeMilestone2: (state, action: PayloadAction<ILifeMilestone2>) => {
      state.life_milestone_2 = action.payload;
    },
    setLifeMilestone3: (state, action: PayloadAction<ILifeMilestone3>) => {
      state.life_milestone_3 = action.payload;
    },
    setSpecialisationCarousel3000: (
      state,
      action: PayloadAction<ISpecialisationCarousel>,
    ) => {
      state.specialisation_carousel_3000 = action.payload;
    },
    setEnrollments1: (state, action: PayloadAction<IEnrollments>) => {
      state.enrollments_1 = action.payload;
    },
    setEnrollments2: (state, action: PayloadAction<IEnrollments2>) => {
      state.enrollments_2 = action.payload;
    },
    setEnrollments3: (state, action: PayloadAction<IEnrollments3>) => {
      state.enrollments_3 = action.payload;
    },
    setEnrollments4: (state, action: PayloadAction<IEnrollments4>) => {
      state.enrollments_4 = action.payload;
    },
    setEnrollments5: (state, action: PayloadAction<IEnrollments5>) => {
      state.enrollments_5 = action.payload;
    },
    setSuccessStories1: (state, action: PayloadAction<ISuccessStories>) => {
      state.success_stories_1 = action.payload;
    },
    setSuccessStories2: (state, action: PayloadAction<ISuccessStories>) => {
      state.success_stories_2 = action.payload;
    },
    setWhatMakesCourseDifferent1: (state, action: PayloadAction<IWhatMakesCourseDifferent>) => {
      state.what_makes_course_different_1 = action.payload;
    },
    setWhatMakesCourseDifferent2: (state, action: PayloadAction<IWhatMakesCourseDifferent>) => {
      state.what_makes_course_different_2 = action.payload;
    },
    setViceChancellor1: (state, action: PayloadAction<IViceChancellor>) => {
      state.vice_chancellor_1 = action.payload;
    },
    setFaculty1: (state, action: PayloadAction<IFaculty>) => {
      state.faculty_1 = action.payload;
    },
    setFaculty2: (state, action: PayloadAction<IFaculty2>) => {
      state.faculty_2 = action.payload;
    },
    setFaculty4: (state, action: PayloadAction<IFaculty4>) => {
      state.faculty_4 = action.payload;
    },
    setFaculty5: (state, action: PayloadAction<IFaculty5>) => {
      state.faculty_5 = action.payload;
    },
    setFaculty6: (state, action: PayloadAction<IFaculty6>) => {
      state.faculty_6 = action.payload;
    },
    setFaculty3: (state, action: PayloadAction<IFaculty3>) => {
      state.faculty_3 = action.payload;
    },
    setTestimonials1: (state, action: PayloadAction<ITestimonials>) => {
      state.testimonials_1 = action.payload;
    },
    setTestimonials2: (state, action: PayloadAction<ITestimonials2>) => {
      const p = action.payload;
      state.testimonials_2 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setMasterclass1: (state, action: PayloadAction<IMasterclass>) => {
      state.masterclass_1 = action.payload;
    },
    setPlacements1: (state, action: PayloadAction<IPlacements>) => {
      state.placements_1 = action.payload;
    },
    setPlacements2: (state, action: PayloadAction<IPlacements2>) => {
      state.placements_2 = action.payload;
    },
    setPlacements3: (state, action: PayloadAction<IPlacements3>) => {
      state.placements_3 = action.payload;
    },
    setPlacements4: (state, action: PayloadAction<IPlacements4>) => {
      state.placements_4 = action.payload;
    },
    setMobileApp1: (state, action: PayloadAction<IMobileApp>) => {
      state.mobile_app_1 = action.payload;
    },
    setFooter1: (state, action: PayloadAction<IFooter>) => {
      const p = action.payload;
      state.footer_1 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setFooter2: (state, action: PayloadAction<IFooter>) => {
      state.footer_2 = action.payload;
    },
    setFooter3: (state, action: PayloadAction<IFooter>) => {
      const p = action.payload;
      state.footer_3 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setSubscribe1: (state, action: PayloadAction<ISubscribe>) => {
      state.subscribe_1 = action.payload;
    },
    setProgramCards1: (state, action: PayloadAction<IProgramCards>) => {
      state.program_cards_1 = action.payload;
    },
    setProgramCards2: (state, action: PayloadAction<IProgramCards>) => {
      const p = action.payload;
      state.program_cards_2 = {
        ...p,
        show_features: Boolean(p.show_features),
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
        use_categories: !(p.use_categories === false || p.use_categories === 0),
      };
    },
    setProgramCards3: (state, action: PayloadAction<IProgramCards>) => {
      const p = action.payload;
      state.program_cards_3 = {
        ...p,
        use_categories: !(p.use_categories === false || p.use_categories === 0),
      };
    },
    setProgramCards4: (state, action: PayloadAction<IProgramCards>) => {
      state.program_cards_4 = action.payload;
    },
    setProgramCards5: (state, action: PayloadAction<IProgramCards>) => {
      state.program_cards_5 = action.payload;
    },
    setThankYou1: (state, action: PayloadAction<IThankYou>) => {
      state.thank_you_1 = action.payload;
    },
    setThankYou1009: (state, action: PayloadAction<IThankYou>) => {
      state.thank_you_1009 = action.payload;
    },
    setThankYou1073: (state, action: PayloadAction<IThankYou1073>) => {
      state.thank_you_1073 = action.payload;
    },
    setRichTextEditor1: (state, action: PayloadAction<IRichTextEditor>) => {
      state.rich_text_editor_1 = action.payload;
    },

    setMoocForm3000: (state, action: PayloadAction<PageState["mooc_form_3000"]>) => {
      state.mooc_form_3000 = action.payload;
    },

    // ── KUK auxiliary pages (3001) ──────────────────────────────────────────
    setBanner3001: (
      state,
      action: PayloadAction<PageState["banner_3001"]>,
    ) => {
      state.banner_3001 = action.payload;
    },

    setBanner5004: (
      state,
      action: PayloadAction<PageState["banner_5004"]>,
    ) => {
      state.banner_5004 = action.payload;
    },

    // ── KUK landing pages — section widgets (5006-5016) ─────────────────────
    setKeyNumbers5006: (state, action: PayloadAction<PageState["key_numbers_5006"]>) => {
      state.key_numbers_5006 = action.payload;
    },
    setKeyNumbers5007: (state, action: PayloadAction<PageState["key_numbers_5007"]>) => {
      state.key_numbers_5007 = action.payload;
    },
    setProgramBenefits5008: (state, action: PayloadAction<PageState["program_benefits_5008"]>) => {
      state.program_benefits_5008 = action.payload;
    },
    setProgramBenefits5009: (state, action: PayloadAction<PageState["program_benefits_5009"]>) => {
      state.program_benefits_5009 = action.payload;
    },
    setRecruiters5010: (state, action: PayloadAction<PageState["recruiters_5010"]>) => {
      state.recruiters_5010 = action.payload;
    },
    setPreLoginReferral5019: (
      state,
      action: PayloadAction<PageState["pre_login_referral_5019"]>,
    ) => {
      state.pre_login_referral_5019 = action.payload;
    },
    setPostLoginReferral5020: (
      state,
      action: PayloadAction<PageState["post_login_referral_5020"]>,
    ) => {
      state.post_login_referral_5020 = action.payload;
    },
    setReferFriend5021: (
      state,
      action: PayloadAction<PageState["refer_friend_5021"]>,
    ) => {
      state.refer_friend_5021 = action.payload;
    },
    setLaunchpadVideo5023: (
      state,
      action: PayloadAction<PageState["launchpad_video_5023"]>,
    ) => {
      state.launchpad_video_5023 = action.payload;
    },
    setLaunchpadVideo5024: (
      state,
      action: PayloadAction<PageState["launchpad_video_5024"]>,
    ) => {
      state.launchpad_video_5024 = action.payload;
    },
    setDashboardAccess5025: (
      state,
      action: PayloadAction<PageState["dashboard_access_5025"]>,
    ) => {
      state.dashboard_access_5025 = action.payload;
    },
    setReferralDashboard5022: (
      state,
      action: PayloadAction<PageState["referral_dashboard_5022"]>,
    ) => {
      state.referral_dashboard_5022 = action.payload;
    },
    setApplicationProcess5011: (state, action: PayloadAction<PageState["application_process_5011"]>) => {
      state.application_process_5011 = action.payload;
    },
    setEligibility5012: (state, action: PayloadAction<PageState["eligibility_5012"]>) => {
      state.eligibility_5012 = action.payload;
    },
    setFaq5013: (state, action: PayloadAction<PageState["faq_5013"]>) => {
      state.faq_5013 = action.payload;
    },
    setFooter5014: (state, action: PayloadAction<PageState["footer_5014"]>) => {
      state.footer_5014 = action.payload;
    },
    setHeader5015: (state, action: PayloadAction<PageState["header_5015"]>) => {
      state.header_5015 = action.payload;
    },
    setHeader5016: (state, action: PayloadAction<PageState["header_5016"]>) => {
      state.header_5016 = action.payload;
    },

    // ── KUK home page (kukonline.ac.in) — variants 1051-1060 ────────────────
    setBanner1051: (state, action: PayloadAction<PageState["banner_1051"]>) => {
      state.banner_1051 = action.payload;
    },
    setAccreditation1052: (state, action: PayloadAction<PageState["accreditation_1052"]>) => {
      state.accreditation_1052 = action.payload;
    },
    setProgramCards1053: (state, action: PayloadAction<PageState["program_cards_1053"]>) => {
      state.program_cards_1053 = action.payload;
    },
    setAbout1054: (state, action: PayloadAction<PageState["about_1054"]>) => {
      state.about_1054 = action.payload;
    },
    setKeyNumbers1055: (state, action: PayloadAction<PageState["key_numbers_1055"]>) => {
      state.key_numbers_1055 = action.payload;
    },
    setTestimonials1056: (state, action: PayloadAction<PageState["testimonials_1056"]>) => {
      state.testimonials_1056 = action.payload;
    },
    setAbout1057: (state, action: PayloadAction<PageState["about_1057"]>) => {
      state.about_1057 = action.payload;
    },
    setBanner1058: (state, action: PayloadAction<PageState["banner_1058"]>) => {
      state.banner_1058 = action.payload;
    },
    setBanner1059: (state, action: PayloadAction<PageState["banner_1059"]>) => {
      state.banner_1059 = action.payload;
    },
    setMenuItem1060: (state, action: PayloadAction<PageState["menu_item_1060"]>) => {
      state.menu_item_1060 = action.payload;
    },
    setBanner1061: (state, action: PayloadAction<PageState["banner_1061"]>) => {
      state.banner_1061 = action.payload;
    },
    setBanner1062: (state, action: PayloadAction<PageState["banner_1062"]>) => {
      state.banner_1062 = action.payload;
    },
    setProgramBenefits1063: (state, action: PayloadAction<PageState["program_benefits_1063"]>) => {
      state.program_benefits_1063 = action.payload;
    },
    setProgramStructure1064: (state, action: PayloadAction<PageState["program_structure_1064"]>) => {
      state.program_structure_1064 = action.payload;
    },
    setElective1065: (state, action: PayloadAction<PageState["elective_1065"]>) => {
      state.elective_1065 = action.payload;
    },
    setProgramFee1066: (state, action: PayloadAction<PageState["program_fee_1066"]>) => {
      state.program_fee_1066 = action.payload;
    },
    setProgramCards1067: (state, action: PayloadAction<PageState["program_cards_1067"]>) => {
      state.program_cards_1067 = action.payload;
    },
    setBanner1068: (state, action: PayloadAction<PageState["banner_1068"]>) => {
      state.banner_1068 = action.payload;
    },
    setProgramBenefits1069: (state, action: PayloadAction<PageState["program_benefits_1069"]>) => {
      state.program_benefits_1069 = action.payload;
    },
    setProgramCards1070: (state, action: PayloadAction<PageState["program_cards_1070"]>) => {
      state.program_cards_1070 = action.payload;
    },

    // ── BDU auxiliary pages (6000+) ─────────────────────────────────────────
    setBanner6000: (
      state,
      action: PayloadAction<PageState["banner_6000"]>,
    ) => {
      state.banner_6000 = action.payload;
    },
    setRichTextEditor6001: (
      state,
      action: PayloadAction<PageState["rich_text_editor_6001"]>,
    ) => {
      state.rich_text_editor_6001 = action.payload;
    },
    setRichTextEditor6003: (
      state,
      action: PayloadAction<PageState["rich_text_editor_6003"]>,
    ) => {
      state.rich_text_editor_6003 = action.payload;
    },
    setRichTextEditor3000: (
      state,
      action: PayloadAction<PageState["rich_text_editor_3000"]>,
    ) => {
      state.rich_text_editor_3000 = action.payload;
    },
    setBlogWithLead1: (state, action: PayloadAction<IBlogWithLead>) => {
      state.blog_with_lead_1 = action.payload;
    },
    setLatestBlogs1: (state, action: PayloadAction<ILatestBlogs>) => {
      state.latest_blogs_1 = action.payload;
    },
    setLatestBlogs2: (state, action: PayloadAction<ILatestBlogs>) => {
      state.latest_blogs_2 = action.payload;
    },
    setPopularBlogs1: (state, action: PayloadAction<IPopularBlogs>) => {
      state.popular_blogs_1 = action.payload;
    },
    setBlogList1: (state, action: PayloadAction<IBlogList>) => {
      state.blog_list_1 = action.payload;
    },
    setCounselorCallCta1: (state, action: PayloadAction<ICounselorCallCta>) => {
      state.counselor_call_cta_1 = action.payload;
    },
    setBreadcrumbBanner1: (
      state,
      action: PayloadAction<IBreadcrumbBanner>
    ) => {
      state.breadcrumb_banner_1 = action.payload;
    },
    setBlogInlineImage1: (
      state,
      action: PayloadAction<IBlogInlineImage>,
    ) => {
      state.blog_inline_image_1 = action.payload;
    },
    setParaText1: (state, action: PayloadAction<IParaText>) => {
      state.para_text_1 = action.payload;
    },
    setHeading1: (state, action: PayloadAction<IHeading>) => {
      state.heading_1 = action.payload;
    },
    setInternshipHeading7000: (
      state,
      action: PayloadAction<IInternshipHeading>
    ) => {
      state.internship_heading_7000 = action.payload;
    },
    setInternshipAbout7001: (
      state,
      action: PayloadAction<IInternshipAbout>
    ) => {
      state.internship_about_7001 = action.payload;
    },
    setInternshipRegistrationProcess7002: (
      state,
      action: PayloadAction<IInternshipRegistrationProcess>
    ) => {
      state.internship_registration_process_7002 = action.payload;
    },
    setInternshipCourses7003: (
      state,
      action: PayloadAction<IInternshipCourses>
    ) => {
      state.internship_courses_7003 = action.payload;
    },
    setHeader5001: (state, action: PayloadAction<IHeader5001>) => {
      state.header_5001 = action.payload;
    },
    setHeader5000: (state, action: PayloadAction<IHeaderPromoPill>) => {
      state.header_5000 = action.payload;
    },
    setAuthorProfile3000: (state, action: PayloadAction<IAuthorProfile>) => {
      state.author_profile_3000 = action.payload;
    },
    setAuthorCard3000: (state, action: PayloadAction<IAuthorCard>) => {
      state.author_card_3000 = action.payload;
    },
    setAuthorByline3000: (state, action: PayloadAction<IAuthorByline>) => {
      state.author_byline_3000 = action.payload;
    },
    setReferralForm1: (state, action: PayloadAction<IReferralForm>) => {
      state.referral_form_1 = action.payload;
    },
    setReferralForm1008: (state, action: PayloadAction<IReferralForm1008>) => {
      state.referral_form_1008 = action.payload;
    },
    setReferralForm1048: (state, action: PayloadAction<IReferralForm1048>) => {
      state.referral_form_1048 = action.payload;
    },
    setReferralForm1071: (state, action: PayloadAction<IReferralForm1071>) => {
      state.referral_form_1071 = action.payload;
    },
    setGrievanceForm3000: (state, action: PayloadAction<IGrievanceForm>) => {
      state.grievance_form_3000 = action.payload;
    },
    setInfluencerForm1: (state, action: PayloadAction<IInfluencerForm>) => {
      state.influencer_form_1 = action.payload;
    },
    setInfluencerForm1007: (
      state,
      action: PayloadAction<IInfluencerForm1007>
    ) => {
      state.influencer_form_1007 = action.payload;
    },
    setInfluencerForm1049: (
      state,
      action: PayloadAction<IInfluencerForm1049>
    ) => {
      state.influencer_form_1049 = action.payload;
    },
    setInfluencerForm1072: (
      state,
      action: PayloadAction<IInfluencerForm1072>
    ) => {
      state.influencer_form_1072 = action.payload;
    },
    setMobileLeadForm1: (state, action: PayloadAction<IMobileLeadForm>) => {
      state.mobile_lead_form_1 = action.payload;
    },
    setMobileLeadForm2: (state, action: PayloadAction<IMobileLeadForm2>) => {
      state.mobile_lead_form_2 = action.payload;
    },
    setMobileLeadForm6: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.mobile_lead_form_6 = action.payload as typeof state.mobile_lead_form_6;
    },
    setCounseling1: (state, action: PayloadAction<ICounseling>) => {
      state.counseling_1 = action.payload;
    },
    setProgramProspects1: (state, action: PayloadAction<IProgramProspects>) => {
      state.program_prospects_1 = action.payload;
    },
    setCourseSpecialisations1: (state, action: PayloadAction<ICourseSpecialisations>) => {
      state.course_specialisations_1 = action.payload;
    },
    setAccordion1: (state, action: PayloadAction<IAccordion>) => {
      state.accordion_1 = action.payload;
    },
    setLiveLecture1: (state, action: PayloadAction<ILiveLectureConfig>) => {
      state.live_lecture_1 = action.payload;
    },
    setLiveLecture1011: (state, action: PayloadAction<ILiveLectureConfig>) => {
      state.live_lecture_1011 = action.payload;
    },
    setLiveLecture1050: (state, action: PayloadAction<ILiveLectureConfig>) => {
      state.live_lecture_1050 = action.payload;
    },
    setProgramBenefits1: (state, action: PayloadAction<IProgramBenefits>) => {
      const p = action.payload;
      state.program_benefits_1 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setProgramBenefits12: (state, action: PayloadAction<IProgramBenefits>) => {
      state.program_benefits_12 = action.payload;
    },
    setProgramBenefits3: (state, action: PayloadAction<IProgramBenefits>) => {
      state.program_benefits_3 = action.payload;
    },
    setProgramBenefits4: (state, action: PayloadAction<IProgramBenefits>) => {
      state.program_benefits_4 = action.payload;
    },
    setProgramBenefits6: (state, action: PayloadAction<IProgramBenefits>) => {
      state.program_benefits_6 = action.payload;
    },
    setProgramBenefits7: (state, action: PayloadAction<IProgramBenefits>) => {
      state.program_benefits_7 = action.payload;
    },
    setProgramBenefits1005: (state, action: PayloadAction<IProgramBenefits>) => {
      state.program_benefits_1005 = action.payload;
    },
    setProgramBenefits2: (state, action: PayloadAction<IProgramBenefits2>) => {
      state.program_benefits_2 = action.payload;
    },
    setCaseStudy1: (state, action: PayloadAction<ICaseStudy>) => {
      const p = action.payload;
      state.case_study_1 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setBrand1: (state, action: PayloadAction<IBrand>) => {
      const p = action.payload;
      state.brand_1 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setInstitutes1: (state, action: PayloadAction<IInstitutes>) => {
      const p = action.payload;
      state.institutes_1 = {
        ...p,
        enable_animation: Boolean(
          p.enable_animation === true || p.enable_animation === 1,
        ),
      };
    },
    setCourseOutcome1: (state, action: PayloadAction<ICourseOutcome>) => {
      state.course_outcome_1 = action.payload;
    },
    setSkillCards3000: (state, action: PayloadAction<ISkillCards>) => {
      state.skill_cards_3000 = action.payload;
    },
    resetPageState: () => initialState,
  },
});

// Export actions
export const {
  setBanner1,
  setBanner2,
  setBanner3,
  setBanner4,
  setBanner5,
  setBanner6,
  setBanner8,
  setBanner15,
  setBanner1010,
  setBanner9,
  setBanner10,
  setBanner11,
  setBanner3000,
  setBanner1025,
  setKeyNumbers1026,
  setProgramCards1027,
  setAbout1028,
  setProgramBenefits1029,
  setApplicationProcess1030,
  setAcademicDeliverables1031,
  setLeaders1032,
  setFaq1033,
  setFooter1034,
  setBanner1035,
  setMoocForm3000,
  setBanner3001,
  setBanner5004,
  setKeyNumbers5006,
  setKeyNumbers5007,
  setProgramBenefits5008,
  setProgramBenefits5009,
  setRecruiters5010,
  setPreLoginReferral5019,
  setPostLoginReferral5020,
  setReferFriend5021,
  setLaunchpadVideo5023,
  setLaunchpadVideo5024,
  setDashboardAccess5025,
  setReferralDashboard5022,
  setApplicationProcess5011,
  setEligibility5012,
  setFaq5013,
  setFooter5014,
  setHeader5015,
  setHeader5016,
  setBanner1051,
  setAccreditation1052,
  setProgramCards1053,
  setAbout1054,
  setKeyNumbers1055,
  setTestimonials1056,
  setAbout1057,
  setBanner1058,
  setBanner1059,
  setMenuItem1060,
  setBanner1061,
  setBanner1062,
  setProgramBenefits1063,
  setProgramStructure1064,
  setElective1065,
  setProgramFee1066,
  setProgramCards1067,
  setBanner1068,
  setProgramBenefits1069,
  setProgramCards1070,
  setBanner6000,
  setKeyNumbers1036,
  setAbout1037,
  setAbout5005,
  setProgramCards1038,
  setKeyNumbers1039,
  setProgramBenefits1040,
  setEligibility1041,
  setProgramStructure1042,
  setProgramFee1043,
  setProgramBenefits1044,
  setProgramCards1045,
  setProgramCards6002,
  setProgramCards6004,
  setHeader1,
  setHeader2,
  setHeader3,
  setHeader4,
  setHeader5,
  setHeader6,
  setHeader7,
  setHeader1046,
  setMenuItem,
  setMenuItem2,
  setMenuItem3,
  setMenuItem4,
  setMenuItem5,
  setMenuItem2000,
  setMenuItem1047,
  setAbout1,
  setAbout2,
  setAbout3,
  setAbout4,
  setAbout5,
  setKeyNumbers1,
  setKeyNumbers2,
  setDigitalLearning1,
  setAccreditation1,
  setAccreditation2,
  setAccreditation3,
  setAccreditation4,
  setAccreditation5,
  setAccreditation1000,
  setAcademicDeliverables1,
  setAcademicDeliverables2,
  setAcademicDeliverables1001,
  setAcademicDeliverables1002,
  setAcademicDeliverables1006,
  setScholar1,
  setFaq1,
  setFaq2,
  setVisionaries1,
  setFaq3,
  setFaq4,
  setLearnFromLeaders,
  setLearnFromLeaders1003,
  setProgramStructure1,
  setElective1,
  setProgramFee1,
  setProgramFee2,
  setProgramStructure2,
  setProgramStructure3,
  setProgramStructure4,
  setProgramStructure5,
  setRecruiters1,
  setEligibility1,
  setEligibility2,
  setSpecialisation1,
  setSpecialisation5017,
  setCareerProspects1,
  setCareerProspects2,
  setCareerProspects3,
  setCareerProspects4,
  setCertification1,
  setApplicationProcess1,
  setApplicationProcess2,
  setSkills1,
  setComparison1,
  setComparison1004,
  setLeaders1,
  setWhoIsThisProgramFor1,
  setBannerVideo1,
  setWalkThrough1,
  setLifeMilestone1,
  setLifeMilestone2,
  setLifeMilestone3,
  setSpecialisationCarousel3000,
  setEnrollments1,
  setEnrollments2,
  setEnrollments3,
  setEnrollments4,
  setEnrollments5,
  setSuccessStories1,
  setSuccessStories2,
  setWhatMakesCourseDifferent1,
  setWhatMakesCourseDifferent2,
  setViceChancellor1,
  setFaculty1,
  setFaculty2,
  setFaculty3,
  setFaculty4,
  setFaculty5,
  setFaculty6,
  setTestimonials1,
  setTestimonials2,
  setMasterclass1,
  setPlacements1,
  setPlacements2,
  setPlacements3,
  setPlacements4,
  setMobileApp1,
  setFooter1,
  setFooter2,
  setFooter3,
  setSubscribe1,
  setProgramCards1,
  setProgramCards2,
  setProgramCards3,
  setProgramCards4,
  setProgramCards5,
  setThankYou1,
  setThankYou1009,
  setThankYou1073,
  setRichTextEditor1,
  setRichTextEditor6001,
  setRichTextEditor6003,
  setRichTextEditor3000,
  setBlogWithLead1,
  setLatestBlogs1,
  setLatestBlogs2,
  setPopularBlogs1,
  setBlogList1,
  setCounselorCallCta1,
  setBreadcrumbBanner1,
  setBlogInlineImage1,
  setParaText1,
  setHeading1,
  setInternshipHeading7000,
  setInternshipAbout7001,
  setInternshipRegistrationProcess7002,
  setInternshipCourses7003,
  setHeader5001,
  setHeader5000,
  setAuthorProfile3000,
  setAuthorCard3000,
  setAuthorByline3000,
  setReferralForm1,
  setReferralForm1008,
  setReferralForm1048,
  setReferralForm1071,
  setGrievanceForm3000,
  setInfluencerForm1,
  setInfluencerForm1007,
  setInfluencerForm1049,
  setInfluencerForm1072,
  setMobileLeadForm1,
  setMobileLeadForm2,
  setMobileLeadForm6,
  setCounseling1,
  setProgramProspects1,
  setCourseSpecialisations1,
  setAccordion1,
  setLiveLecture1,
  setLiveLecture1011,
  setLiveLecture1050,
  setProgramBenefits1,
  setProgramBenefits12,
  setProgramBenefits3,
  setProgramBenefits4,
  setProgramBenefits6,
  setProgramBenefits7,
  setProgramBenefits1005,
  setProgramBenefits2,
  setCaseStudy1,
  setBrand1,
  setInstitutes1,
  setCourseOutcome1,
  resetPageState,
  setSkillCards3000,
} = pageSlice.actions;

// Selectors
export const selectCurrentPageContent = (state: { page: PageState }) =>
  state.page;

// Export reducer
export default pageSlice.reducer;
